/**
 * Renders an interactive Mapbox map for the "Product explore experiences"
 * section. Destinations are loaded from an API and drawn as bubbles whose size
 * and colour scale with how many experiences they hold; clicking one opens a
 * panel listing them.
 */
const MAPBOX_GL_VERSION = '3.9.0';
let mapboxLoadPromise = null;

function loadMapboxGl() {
  if (typeof mapboxgl !== 'undefined') return Promise.resolve();
  if (mapboxLoadPromise) return mapboxLoadPromise;

  mapboxLoadPromise = new Promise((resolve, reject) => {
    if (!document.querySelector('[data-mapbox-gl-css]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = `https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_GL_VERSION}/mapbox-gl.css`;
      link.dataset.mapboxGlCss = 'true';
      document.head.appendChild(link);
    }

    const existingScript = document.querySelector('[data-mapbox-gl-js]');
    if (existingScript) {
      if (typeof mapboxgl !== 'undefined') {
        resolve();
        return;
      }
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = `https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_GL_VERSION}/mapbox-gl.js`;
    script.dataset.mapboxGlJs = 'true';
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return mapboxLoadPromise;
}

async function fetchJson(url) {
  // snippets/experiences-prefetch.liquid may already have this request in flight,
  // started from the top of the product page rather than after this deferred file
  // downloads. Taken once and removed: a Response body reads only once, and a
  // reload() for another SKU must go back to the network.
  const warmed = window.__ewExperiencesPrefetch?.get(url);
  if (warmed) window.__ewExperiencesPrefetch.delete(url);

  const response = await (warmed || fetch(url));
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return response.json();
}

const numberFormatter = new Intl.NumberFormat('en-US');

function formatNumber(value) {
  return numberFormatter.format(Number(value) || 0);
}

/**
 * Copy this file renders itself, rather than reading out of the DOM. The
 * section fills `window.ExploreExperiencesStrings` from `locales/` in an inline
 * script; these are the last-resort defaults for when that block has not run.
 */
const EXPLORE_STRINGS = {
  review_one: 'review',
  review_other: 'reviews',
  reviews: 'reviews',
  experiences_included: 'experiences included',
  see_all_count: '{{ count }}+',
  gesture_zoom_ctrl: 'Use ctrl + scroll to zoom the map',
  gesture_zoom_cmd: 'Use ⌘ + scroll to zoom the map',
  gesture_pan_touch: 'Use two fingers to move the map',
  filter_all_destinations: 'All destinations',
  filter_all_of_country: 'All of {{ country }}',
  filter_person_one: '1 person',
  filter_people_other: '{{ count }} people',
  count_one: '{{ count }} experience',
  count_other: '{{ count }} experiences',
  count_for_people: 'for {{ people }}',
  count_in_destination: 'in {{ destination }}',
  count_in_destination_area: 'in {{ destination }} and {{ area }}',
  progress: 'Showing {{ shown }} of {{ total }} experiences',
  progress_all: 'Showing all {{ total }} experiences',
  filter_search_hint: 'Type to search {{ count }} destinations',
  filter_no_results: 'No destinations match “{{ query }}”',
  filter_country_label: 'Country',
  party_up_to_one: 'For 1 person',
  party_up_to_other: 'Up to {{ count }} people',
  cover_title: 'Fully covered',
  cover_title_party: 'Fully covered {{ party }}',
  cover_line: 'Paid by your gift',
  cover_unit: 'value',
  value_party_2: 'for two',
  value_party_3: 'for three',
  value_party_4: 'for four',
  value_party_5: 'for five',
  value_party_6: 'for six',
  value_party_7: 'for seven',
  value_party_8: 'for eight',
  value_party_9: 'for nine',
  value_party_10: 'for ten',
  value_party_other: 'for {{ count }} people',
};

/** `{{ name }}` placeholders, the same shape `locales/` uses for these keys. */
function exploreStringWith(key, variables) {
  let text = exploreString(key) || '';
  Object.entries(variables).forEach(([name, value]) => {
    text = text.replace(new RegExp(`{{\\s*${name}\\s*}}`, 'g'), String(value));
  });
  return text;
}

function exploreString(key) {
  return window.ExploreExperiencesStrings?.[key] ?? EXPLORE_STRINGS[key];
}

/**
 * Copy for the overlays Mapbox shows when a blocked gesture is attempted under
 * `cooperativeGestures`, keyed the way mapbox-gl's locale table expects.
 * mapbox-gl picks the Cmd variant on Apple platforms itself.
 * @returns {Record<string, string>}
 */
function cooperativeGesturesLocale() {
  return {
    'ScrollZoomBlocker.CtrlMessage': exploreString('gesture_zoom_ctrl'),
    'ScrollZoomBlocker.CmdMessage': exploreString('gesture_zoom_cmd'),
    'TouchPanBlocker.Message': exploreString('gesture_pan_touch'),
  };
}

class SectionProductExploreExperiences {
  /**
   * How many experiences a city needs before the locations line will name it.
   * See `matchDestination`: under this the general "200+ US cities" line is
   * the stronger claim, so it stays.
   */
  static NEAR_MIN_COUNT = 10;

  /**
   * The rendition of a Viator cover photo the peek rail should ask for, given
   * the one the API hands over.
   *
   * Every cover on the catalogue is one file on the Tripadvisor CDN with the
   * rendition baked into the path — `.../attractions-splice-spp-720x480/...` —
   * and the ladder of sizes behind it is fixed: 75, 200, 210, 360, 400, 540,
   * 720. The rail renders ~114pt tiles, so the 720 is roughly 400KB of
   * photography, above Add to cart, to fill four boxes that need a tenth of it.
   *
   * Swapping the token is a deliberate shortcut. The robust version of this
   * reads the variant list server-side and projects a second URL onto the list
   * row; that costs a column, a migration and a whole-catalogue backfill, and
   * this buys the same bytes today. The costs of the shortcut are real and
   * bounded: it knows a partner's URL shape, and it is wrong the moment
   * Tripadvisor changes it. Both are covered — a URL that does not match the
   * pattern comes back untouched, and `paintJumpThumbs` re-points the tile at
   * the full cover if the small one fails to load.
   *
   * Only ever call this for something rendered small. 360x240 in the
   * experiences section's 302x240 cards would be an upscale.
   */
  static COVER_VARIANT_PATTERN = /\/attractions-splice-spp-720x480\//;

  static COVER_THUMB_SEGMENT = '/attractions-splice-spp-360x240/';

  static thumbnailUrl(url) {
    if (typeof url !== 'string') return url;
    return url.replace(
      SectionProductExploreExperiences.COVER_VARIANT_PATTERN,
      SectionProductExploreExperiences.COVER_THUMB_SEGMENT,
    );
  }

  constructor(container) {
    this.container = container;
    // The section renders the opening view (and the matching toggle order and
    // `hidden` classes) from `ew_pdp.experiences_map_view`, so start from what
    // the markup already says rather than assuming the map.
    this.view = container.dataset.defaultView === 'map' ? 'map' : 'list';
    /** Page 1 of the unified list, curated rows first — see getExperiences. */
    this.experiences = [];
    this.experiencesResponse = null;
    /** The map's ring data, fetched only once the map boots — see loadCurated. */
    this.curated = [];
    this.mapPoints = null;
    this.listsInitialized = false;
    this.experiencesList = null;
    this.mapRoot = null;
    this._loadPromise = null;
    this._curatedPromise = null;
    /**
     * Which load owns the section. A variant change starts a new one while
     * the old one's three requests are still in flight, and without this the
     * slower answer wins simply by landing last — the list then shows one
     * SKU's experiences under another SKU's endpoints.
     */
    this._loadGeneration = 0;
    this._mapInitPromise = null;
    this._mapObserver = null;
  }

  static onLoad() {
    const containers = document.querySelectorAll('.explore_experiences');
    // A page may ask for its own page size on the section (product page B: 6); every URL and
    // Load more step then uses it. The endpoint clamps `size` to 1…50.
    const pageSize = Number(containers[0]?.dataset.pageSize);
    if (pageSize >= 1 && pageSize <= 50)
      ProductExperiencesList.REQUESTED_PAGE_SIZE = pageSize;
    SectionProductExploreExperiences.instances = Array.from(
      containers,
      (container) => new SectionProductExploreExperiences(container),
    );
    SectionProductExploreExperiences.instances.forEach((instance) =>
      instance.start(),
    );
  }

  static reloadForVariant(sku) {
    const instances = SectionProductExploreExperiences.instances || [];
    instances.forEach((instance) => instance.reload(sku));
  }

  async start() {
    // The endpoints hang off the section, not the map: with no Mapbox token the
    // map root is never rendered, and the lists and the catalogue counts still
    // need them.
    this.mapRoot = this.container.querySelector(
      ExploreExperiencesMap.SELECTORS.root,
    );
    // Before the round trip, not after it: the strip is on screen from first
    // paint now, so a tap during the load has to queue rather than land on a
    // button with no handler.
    this.bindJumpButtons();
    this.bindLocationLine();

    const { apiBaseUrl: baseUrl, productSku: sku } = this.container.dataset;
    // Nothing to query with, so nothing can ever populate — drop the section
    // now rather than after a round trip that will not happen.
    if (!baseUrl || !sku) {
      this.container.classList.add('hidden');
      this.syncJumpButtons(false);
      return;
    }

    await this.loadForSku(baseUrl, sku);
  }

  /**
   * The section is a shell around API data: the list and the map bubbles come
   * from /experiences and /map-points. If both come back empty — or fail,
   * which the getters flatten to empty — there is nothing to explore, so the
   * whole section is hidden instead of being left as an empty frame.
   *
   * Only what this section actually renders counts: with no Mapbox token there
   * is no map root, so for it the answer comes down to the list.
   */
  updateSectionVisibility() {
    const hasContent = Boolean(
      this.experiences.length || (this.mapRoot && this.mapPoints?.length),
    );

    this.container.classList.toggle('hidden', !hasContent);
    this.syncJumpButtons(hasContent);
    // Unconditionally: an empty answer hides the strip, but it must not leave the
    // dimmed variant-change state latched on for the next SKU that does have rows.
    this.setThumbsLoading(false);
    // Not again for rows the first load already painted ahead of /map-points
    // (`_loadForSku`): a repaint would restart the tiles' entrance.
    if (hasContent && this._thumbsPaintedFor !== this.experiences)
      this.paintJumpThumbs();
    this._thumbsPaintedFor = null;
  }

  /**
   * Dims the peek while a variant change refetches the list. The tiles on screen
   * belong to the SKU the shopper just left, and tearing them down would put the
   * skeleton back for a rail that already has photography in it — so they stay,
   * at 45%, and the new ones fade in over the top. Styled in app.scss.
   */
  setThumbsLoading(loading) {
    document.querySelectorAll('[data-experiences-thumbs]').forEach((holder) => {
      holder.toggleAttribute('data-experiences-thumbs-loading', loading);
    });
  }

  /**
   * Fills the PDP strip with a peek at the list: miniatures of its first page,
   * which opens with the curated picks whenever the SKU has any, because the
   * API puts them first. Photo, rating, title and location — the same facts,
   * in the same order, that the cards in the experiences section carry, so
   * the peek and the details a tile opens read as the same object. Not the
   * "Editor's pick" pill: on the first page every row is one.
   *
   * The markup is cloned from Liquid <template>s rather than built here,
   * because Tailwind only scans the Liquid directories — classes written in this
   * file never reach the compiled stylesheet. For the same reason nothing here
   * sizes anything: every tile is a fixed height set in that template, and
   * app.scss reserves exactly that height before this runs, so filling the rail
   * never moves Add to cart.
   *
   * Every row of the first page goes in — the rail is a swipeable row — and
   * the "See more" card closes it. Nothing is paged in behind a swipe: the
   * rail sits above Add to cart, where its job is to say what is in the gift,
   * and a second place to browse would compete with the list built for
   * browsing while quietly loading pages into it. Past the last tile the card
   * hands over to that list.
   *
   * Each tile carries the id of its row (`ExperiencesList.idOf`, the same key
   * the list's cards are written with); `bindJumpButtons` reads it back on a
   * tap and opens that experience's details.
   *
   * Rows are taken raw off the API response, not through
   * `ExperiencesList.prepare` — this needs a handful of fields, and running a
   * row through the full normaliser to get them would tie the peek to a shape
   * it does not otherwise use. The location line is read the way `prepare`
   * reads it, so the place matches the card's.
   *
   * The photo is asked for at the CDN's 360x240 rendition rather than the
   * 720x480 the API hands over — see `thumbnailUrl` for why, and for what
   * happens when the URL is not the shape it expects. The full cover stays the
   * fallback, wired through the image's own error event rather than assumed.
   * Every image is `loading='lazy'`, so the tiles nobody swipes to cost no
   * bytes.
   */
  paintJumpThumbs() {
    const template = document.querySelector(
      '[data-experiences-thumb-template]',
    );
    const moreTemplate = document.querySelector(
      '[data-experiences-thumb-more-template]',
    );
    if (!template) return;

    const rows = this.experiences.filter((item) => item?.coverImageUrl);

    document.querySelectorAll('[data-experiences-thumbs]').forEach((holder) => {
      // The rail ships with shimmering skeleton tiles in it, so every path out of
      // here has to settle them — including the one where the SKU's rows carry no
      // photography, which leaves the reserved footprint empty exactly as it was
      // before the skeleton existed. Dropping the attribute is also what arms the
      // entrance animation on the tiles appended below (app.scss).
      holder.removeAttribute('data-experiences-thumbs-pending');
      holder.replaceChildren();
      // A variant change repaints a rail the shopper may have swiped along.
      holder.scrollLeft = 0;
      if (!rows.length) return;

      rows.forEach((item) => {
        const tile = template.content.cloneNode(true).firstElementChild;

        const image = tile.querySelector('[data-experiences-thumb-image]');
        const cover = item.coverImageUrl;
        const thumb = SectionProductExploreExperiences.thumbnailUrl(cover);
        // Only when a swap actually happened, and only once: if the full cover
        // fails too there is nothing left to try, and a listener that re-fires
        // would spin.
        if (thumb !== cover) {
          image.addEventListener(
            'error',
            () => {
              image.src = cover;
            },
            { once: true },
          );
        }
        image.src = thumb;
        const title = item.title || item.name || '';
        tile.querySelector('[data-experiences-thumb-title]').textContent =
          title;
        tile.setAttribute('aria-label', title);
        tile.dataset.experiencesThumb = ExperiencesList.idOf(item);

        // "$750 value" across the photo's foot, for a row the API priced —
        // the figure the cards' "Fully covered" strip carries on the right.
        const valueCover = ExperiencesList.valueCover(item.value);
        const value = tile.querySelector('[data-experiences-thumb-value]');
        if (value && valueCover) {
          value.querySelector(
            '[data-experiences-thumb-value-amount]',
          ).textContent = valueCover.amount;
          value.hidden = false;
        }

        // The pin ships hidden like the star below, so a row with no place
        // leaves its fixed-height row blank rather than a pin pointing at
        // nothing. Same `removeAttribute` reasoning: it is an <svg>.
        const location = ExperiencesList.formatLocation(item.location);
        if (location) {
          tile.querySelector('[data-experiences-thumb-location]').textContent =
            location;
          tile
            .querySelector('[data-experiences-thumb-pin]')
            .removeAttribute('hidden');
        }

        // The star ships hidden and the number empty, so a row with no rating
        // leaves the fixed-height row blank rather than showing a lone star or
        // a "0.0". Same field pair ExperiencesList.prepare reads.
        //
        // `removeAttribute`, not `.hidden = false`: the star is an <svg>, and
        // `hidden` is defined on HTMLElement — assigning it on an SVGElement
        // sets a property nothing reads and leaves the attribute in place, so
        // the star stayed invisible while the number beside it rendered.
        const rating = Number(item.combinedAverageRating ?? item.rating);
        if (Number.isFinite(rating) && rating > 0) {
          tile
            .querySelector('[data-experiences-thumb-star]')
            .removeAttribute('hidden');
          tile.querySelector('[data-experiences-thumb-rating]').textContent =
            rating.toFixed(1);
        }

        holder.append(tile);
      });

      if (moreTemplate) {
        const more = moreTemplate.content.cloneNode(true).firstElementChild;
        // "10,400+" under "See all": the merchant's headline total for the
        // SKU on screen, the same figure the progress line under the list
        // caps itself with. Read off the section's SKU → total map here
        // rather than through `ProductExperiencesList.variantTotal`, because
        // the rail is painted before that list exists. Left hidden — the
        // template ships it so — when the variant has no total set.
        const total = this.variantTotal();
        const count = more.querySelector('[data-experiences-thumb-more-count]');
        if (count && total) {
          count.textContent = exploreStringWith('see_all_count', {
            count: formatNumber(total),
          });
          count.hidden = false;
        }
        holder.append(more);
      }
    });
  }

  /**
   * The merchant's `ew_pdp.experience_count` for the SKU on screen, from the
   * map the section renders in `data-experiences-totals`; the same lookup
   * `ProductExperiencesList.variantTotal` does, on the same attributes.
   *
   * @returns {number|null} null when the variant has no total set.
   */
  variantTotal() {
    const { productSku: sku, experiencesTotals: raw } = this.container.dataset;
    if (!sku || !raw) return null;
    try {
      const total = Number(JSON.parse(raw)[sku]);
      return total > 0 ? total : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Opens one experience's details from a peek tile — the same panel, fed the
   * same way, as a card in the experiences section. The list owns the modal,
   * so this waits for the load that builds it, and asks the section to reveal
   * itself first: the dialog lives inside the deferred-render subtree, and a
   * `content-visibility: hidden` ancestor has nothing to show.
   *
   * @param {string} experienceId - `ExperiencesList.idOf` of the tapped row.
   */
  async openExperienceFromThumb(experienceId) {
    if (this._loadPromise) await this._loadPromise;
    const item = this.experiences.find(
      (row) => ExperiencesList.idOf(row) === experienceId,
    );
    if (!item || !this.experiencesList) return;

    this.container.dispatchEvent(new CustomEvent('everwow:experiences:reveal'));
    this.experiencesList.openExperienceDetails(
      ExperiencesList.prepare(item)._experience,
    );
  }

  /**
   * The PDP's "What's included" strip lives in the product section, not in
   * this one, so it is wired from here.
   *
   * Wiring is separate from revealing, and runs first. The strip used to ship
   * hidden and appear with the ready attribute, which meant it landed ~400ms
   * after DOMContentLoaded and pushed Add to cart 90px down the page. It now
   * ships visible (see snippets/experiences-strip.liquid) and this only ever
   * takes it away, so the handlers have to exist before the API answers —
   * `scrollToExperiences` and `openExperienceFromThumb` wait out a load in
   * flight.
   *
   * The rail gets one delegated listener rather than one per tile: its tiles
   * are repainted on every variant change. A tile opens its experience's
   * details; the "See more" card that ends the rail scrolls to the list. The
   * frame around the strip (`[data-experiences-jump]`) is not clickable any
   * more — it only carries the ready flag `syncJumpButtons` toggles.
   *
   * Idempotent because `reload` re-runs the whole load for a new SKU.
   */
  bindJumpButtons() {
    document.querySelectorAll('[data-experiences-thumbs]').forEach((rail) => {
      if (rail.dataset.experiencesThumbsBound) return;
      rail.dataset.experiencesThumbsBound = 'true';
      this.bindThumbDrag(rail);
      this.bindThumbArrows(rail);
      rail.addEventListener('click', (event) => {
        // The tail of a drag, not a tap — see bindThumbDrag.
        if (rail.dataset.experiencesThumbsDragged) {
          delete rail.dataset.experiencesThumbsDragged;
          return;
        }
        const tile = event.target.closest?.('[data-experiences-thumb]');
        if (tile) {
          this.openExperienceFromThumb(tile.dataset.experiencesThumb);
          return;
        }
        const more = event.target.closest?.('[data-experiences-thumb-more]');
        if (more) this.scrollToExperiences(more);
      });
    });

    // The strip's "See all experiences" button, the sticky bar's link and the
    // locations line under the title (snippets/location-line.liquid) all land
    // on the same list, where the shopper can narrow by destination and party
    // size. The locations line reads "Pick their city", so it carries the
    // value `destination` and `scrollToExperiences` hands it the search box.
    document.querySelectorAll('[data-experiences-scroll]').forEach((button) => {
      if (button.dataset.experiencesJumpBound) return;
      button.dataset.experiencesJumpBound = 'true';
      button.addEventListener('click', () => this.scrollToExperiences(button));
    });
  }

  /**
   * Lets a mouse swipe the rail. A finger scrolls a native overflow row by
   * itself; a mouse has nothing but a hidden scrollbar and shift-wheel, so on
   * a desktop the row read as stuck. Mouse pointers only — touch and pen
   * already scroll natively, and taking over would fight them.
   *
   * A drag is a move of 4px or more; under that the tiles keep their click.
   * Past it the pointer is captured so the drag survives leaving the rail,
   * `data-experiences-thumbs-dragging` switches the cursor (app.scss), and
   * the click that the browser fires after the button
   * is released is marked to be ignored, so letting go over a tile does not
   * open it.
   */
  bindThumbDrag(rail) {
    let drag = null;

    const release = () => {
      if (!drag) return;
      const { moved } = drag;
      drag = null;
      rail.toggleAttribute('data-experiences-thumbs-dragging', false);
      if (moved) {
        rail.dataset.experiencesThumbsDragged = 'true';
        // The click, when there is one, fires in the same task; if the
        // button was released with no click to follow, the mark must not
        // wait around to eat the next real tap.
        setTimeout(() => delete rail.dataset.experiencesThumbsDragged, 0);
      }
    };
    rail.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      drag = {
        id: event.pointerId,
        x: event.clientX,
        left: rail.scrollLeft,
        moved: false,
      };
    });

    rail.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      // The button was let go somewhere this listener could not see — a press
      // that never reached the drag threshold has no capture, so a release
      // outside the rail never arrives. Without this the next pass of the
      // mouse over the rail scrolled it with nothing pressed.
      if (!(event.buttons & 1)) {
        release();
        return;
      }
      const dx = event.clientX - drag.x;
      if (!drag.moved) {
        if (Math.abs(dx) < 4) return;
        drag.moved = true;
        rail.setPointerCapture(event.pointerId);
        rail.toggleAttribute('data-experiences-thumbs-dragging', true);
      }
      rail.scrollLeft = drag.left - dx;
    });

    rail.addEventListener('pointerup', release);
    rail.addEventListener('pointercancel', release);
    rail.addEventListener('lostpointercapture', release);
    // The photos are images, and a mouse drag on an image starts a native
    // drag of the file instead of scrolling.
    rail.addEventListener('dragstart', (event) => event.preventDefault());
  }

  /**
   * The rail's two desktop arrows (snippets/experiences-strip.liquid). A drag
   * works, but nothing says it does, and a trackpad's sideways swipe is not
   * something every shopper reaches for — the rail read as a static row of
   * four. Each click moves three-quarters of the rail, so the tile half cut
   * off at the edge is whole and first after it.
   *
   * An arrow is `disabled` while the rail is at its end, which hides it
   * (Tailwind `disabled:`). Kept in step on scroll, on resize, and whenever
   * `paintJumpThumbs` swaps the tiles — a repaint changes how far the row
   * can go without firing a scroll. A focused arrow that becomes disabled
   * hands focus to the other one rather than dropping it on the body.
   */
  bindThumbArrows(rail) {
    const arrows = rail.parentElement?.querySelectorAll(
      '[data-experiences-thumbs-arrow]',
    );
    if (!arrows?.length) return;

    const sync = () => {
      const end = rail.scrollWidth - rail.clientWidth;
      arrows.forEach((arrow) => {
        const back = Number(arrow.dataset.experiencesThumbsArrow) < 0;
        const off = back ? rail.scrollLeft <= 1 : rail.scrollLeft >= end - 1;
        if (off && arrow === document.activeElement) {
          Array.from(arrows)
            .find((other) => other !== arrow)
            ?.focus({ preventScroll: true });
        }
        arrow.disabled = off;
      });
    };

    arrows.forEach((arrow) => {
      arrow.addEventListener('click', () => {
        const step = Number(arrow.dataset.experiencesThumbsArrow);
        const reduce = window.matchMedia(
          '(prefers-reduced-motion: reduce)',
        ).matches;
        rail.scrollBy({
          left: step * rail.clientWidth * 0.75,
          behavior: reduce ? 'auto' : 'smooth',
        });
      });
    });

    rail.addEventListener('scroll', sync, { passive: true });
    new ResizeObserver(sync).observe(rail);
    new MutationObserver(sync).observe(rail, { childList: true });
    sync();
  }

  /**
   * Takes the strip (and the locations line's link) away when a SKU has
   * nothing to open, and gives them back when the next one does. Called after
   * every load, not once. The strip's own controls are inside the frame, so
   * hiding the frame is what hides them.
   */
  syncJumpButtons(hasContent) {
    document
      .querySelectorAll('[data-experiences-jump], [data-experiences-scroll]')
      .forEach((button) => {
        button.toggleAttribute('data-experiences-jump-ready', hasContent);
      });
  }

  /**
   * Scrolls to the experiences list — the one block every PDP CTA lands on.
   * The section holds itself out of the render tree until scrolled near
   * (`data-deferred-render`), and an element inside a
   * `content-visibility: hidden` subtree has no box to scroll to — so the
   * reveal is asked for first, and the scroll waits one frame for the layout
   * it produces. The list view is switched on so the target is actually
   * visible on a product that opens on the map.
   *
   * The locations line's link says what it will do, and the attribute's value
   * says how. `destination` ("Pick their city", "Change city") puts the cursor
   * in the city search once the scroll has landed. `filter` ("See 3,134 near
   * Chicago") selects `data-experiences-destination` instead, so the list is
   * already narrowed to the city the link named.
   *
   * @param {HTMLElement} button - The control that was clicked.
   */
  async scrollToExperiences(button) {
    if (this._loadPromise) {
      button?.toggleAttribute('data-experiences-jump-busy', true);
      try {
        await this._loadPromise;
      } finally {
        button?.toggleAttribute('data-experiences-jump-busy', false);
      }
    }
    if (!button?.hasAttribute('data-experiences-jump-ready')) return;

    const section = this.container;
    section.dispatchEvent(new CustomEvent('everwow:experiences:reveal'));
    this.switchView('list');

    const mode = button.dataset.experiencesScroll;
    // Filtered before the travelling starts, not after it lands: the request
    // goes out while the page is still scrolling, so the narrowed rows are
    // usually already there when the shopper arrives.
    if (mode === 'filter') {
      this.applyDestinationFilter(
        button.dataset.experiencesDestination,
        button.dataset.experiencesDestinationName,
      );
    }

    const target = section.querySelector('[data-experiences-block]') || section;
    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Keyboard users land on the card, so the next Tab reaches the filters.
      // `preventScroll`: the smooth scroll above owns the movement.
      if (typeof target.focus === 'function') {
        target.focus({ preventScroll: true });
      }
      if (mode === 'destination') this.focusDestinationSearch();
    });
  }

  /**
   * Watches for the IP lookup behind the locations line under the product
   * title (snippets/location-line.liquid, assets/location-grabber.js).
   *
   * That script resolves a US visitor's state to its largest city, writes it
   * to `data-geo-city` and announces it. Both halves of the near state — the
   * city and the count of what is in it — are needed before the line can be
   * rewritten, and they arrive from two different places, so the listener and
   * `applyLoadedData` both call the same idempotent upgrade. The dataset is
   * read once up front as well: a cached lookup settles the line before this
   * deferred file has even parsed, and that announcement is long gone.
   */
  bindLocationLine() {
    if (this._locationLineBound) return;
    this._locationLineBound = true;
    document.addEventListener('everwow:geolocated', () =>
      this.upgradeLocationLine(),
    );
    this.upgradeLocationLine();
  }

  /**
   * Rewrites the locations line from the general state to the near state, when
   * the visitor's own city turns out to be a destination this gift covers:
   *
   *   Redeemable in New York, Los Angeles, Chicago and 200+ US cities · Pick their city
   *   Redeemable near Chicago and in 200+ US cities · See 3,134 near Chicago
   *
   * Every string comes off the element as `data-near-*` (merchant copy, see
   * the snippet); the only things this contributes are the city, the number
   * and the destination id the link will filter by. It gives up quietly —
   * leaving the general state, which is true for everyone — whenever any part
   * is missing: no lookup, no matching destination, or a destination with
   * nothing in it.
   *
   * The name written into the line is the *catalogue's* ("New York City"), not
   * the lookup's ("New York"), because it is also the label of the filter the
   * link applies, and a link that says one city and delivers another spends
   * the trust the count just bought.
   */
  upgradeLocationLine() {
    const line = document.querySelector('[data-location-line]');
    if (!line) return;

    const city = line.dataset.geoCity;
    if (!city) return;

    // Nothing to judge the city against yet. Leave the line exactly as the
    // lookup left it and wait for `applyLoadedData` to call again.
    const points = this.mapPoints || [];
    if (!points.length) return;

    const point = SectionProductExploreExperiences.matchDestination(
      city,
      points,
    );
    if (!point) {
      this.restoreGeneralCities(line);
      return;
    }

    const count = Number(point.count) || 0;
    // Re-keyed on the count as well as the id: a variant change keeps the city
    // and moves the number, and the line has to follow it.
    const state = `${point.destinationId}:${count}`;
    if (line.dataset.locationNear === state) return;

    const lead = line.querySelector('[data-location-lead]');
    const cities = line.querySelector('[data-location-cities]');
    const more = line.querySelector('[data-location-more]');
    const link = line.querySelector('[data-location-link]');
    const subtext = line.querySelector('[data-location-subtext]');
    const subtextText = line.querySelector('[data-location-subtext-text]');
    const change = line.querySelector('[data-location-change]');
    if (!lead || !cities || !more || !link) return;

    const name = String(point.name);

    // The near sentence is the shorter of the two and at desktop widths costs
    // a row, so an upgrade that lands after first paint would pull everything
    // under it — the buy box included — up by ~21px. The line keeps the height
    // it already had instead. A resize is a reflow the shopper asked for, and
    // the reservation is stale by then, so it goes.
    const reserved = line.getBoundingClientRect().height;
    if (reserved && !line.style.minHeight) {
      line.style.minHeight = `${reserved}px`;
      window.addEventListener(
        'resize',
        () => {
          line.style.minHeight = '';
        },
        { once: true },
      );
    }

    lead.textContent = line.dataset.nearLead || lead.textContent;
    cities.textContent = name;
    more.textContent = line.dataset.nearMore || '';
    more.hidden = !more.textContent;

    link.textContent = String(line.dataset.nearLink || '')
      .replace('{count}', formatNumber(count))
      .replace('{city}', name);
    // From "open the search" to "apply this destination" — see
    // `scrollToExperiences`.
    link.dataset.experiencesScroll = 'filter';
    link.dataset.experiencesDestination = String(point.destinationId);
    link.dataset.experiencesDestinationName = name;

    if (subtext && subtextText && change) {
      subtextText.textContent = line.dataset.nearSubtext || '';
      change.hidden = false;
      subtext.hidden = false;
    }

    line.dataset.locationNear = state;
  }

  /**
   * Undoes the geolocation swap when the visitor's city turns out not to be a
   * destination this gift covers.
   *
   * assets/location-grabber.js leads the named cities with the largest city of
   * the visitor's state, off a static table, knowing nothing about what is
   * actually in the catalogue. That is how "Redeemable in Bridgeport, New
   * York, Los Angeles…" gets on screen for a gift that has nothing in
   * Bridgeport — the line names the one city in it the shopper cannot use.
   * Here the counts are known, so the merchant's own list goes back.
   *
   * @param {HTMLElement} line - The locations line root.
   */
  restoreGeneralCities(line) {
    // Already upgraded, on an earlier call or for an earlier variant: the near
    // state is the better line and its city is one we matched.
    if (line.dataset.locationNear) return;

    const target = line.querySelector('[data-location-cities]');
    if (!target) return;

    let configured = [];
    try {
      configured = JSON.parse(line.dataset.cities || '[]');
    } catch (error) {
      return;
    }
    if (!Array.isArray(configured) || !configured.length) return;

    const visible = Math.max(
      1,
      Number(line.dataset.visible) || configured.length,
    );
    const text = configured.slice(0, visible).join(', ');
    if (target.textContent !== text) target.textContent = text;
  }

  /**
   * The `/map-points` bubble for a city name, or null.
   *
   * The two vocabularies do not line up: the lookup table in
   * assets/location-grabber.js says "New York" and "Washington DC" where the
   * catalogue says "New York City" and "Washington, D.C.". So the comparison
   * is on folded text and allows either side to be the longer one, with a
   * four-character floor on the shorter so "Nice" cannot swallow a city that
   * merely starts the same way.
   *
   * Every candidate is collected and the biggest one wins, rather than the
   * first rule to fire. An exact name match is *not* good enough on its own:
   * this catalogue holds both a "New York" with 18 experiences up near
   * Rochester and the "New York City" with 736, and matching the name exactly
   * picks the wrong one of the two every time. Volume is the tie-break that
   * gets a New York State visitor the city they meant.
   *
   * A thin bubble is not a candidate at all. The near state exists to put a
   * number in front of the shopper, and it has to be a number that helps:
   * "See 1 near Billings" argues against the gift harder than the general
   * line's "200+ US cities" argues for it, and Billings really does hold
   * exactly one. Below NEAR_MIN_COUNT the general line stands. US bubbles are
   * preferred because the lookup only ever resolves US states, so a same-named
   * town elsewhere is the wrong answer.
   *
   * @param {string} city - The city the IP lookup resolved.
   * @param {object[]} points - Raw /map-points rows.
   * @returns {object|null}
   */
  static matchDestination(city, points) {
    const fold = SectionProductExploreExperiences.foldName;
    const key = fold(city);
    if (!key) return null;

    const usable = (points || []).filter(
      (point) =>
        point &&
        point.destinationId != null &&
        point.name &&
        Number(point.count) >= SectionProductExploreExperiences.NEAR_MIN_COUNT,
    );
    if (!usable.length) return null;

    const inUs = usable.filter((point) =>
      ['usa', 'us', 'unitedstates', 'unitedstatesofamerica'].includes(
        fold(point.country),
      ),
    );
    const pool = inUs.length ? inUs : usable;

    const candidates = pool.filter((point) => {
      const name = fold(point.name);
      if (name === key) return true;
      if (key.length >= 4 && name.startsWith(key)) return true;
      return name.length >= 4 && key.startsWith(name);
    });
    if (!candidates.length) return null;

    return candidates.reduce((best, point) =>
      Number(point.count) > Number(best.count) ? point : best,
    );
  }

  /** Lower-cased, unaccented, stripped to letters and digits. */
  static foldName(text) {
    return String(text || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '');
  }

  /**
   * Selects a destination in the filter bar's city search, which is what makes
   * "See 3,134 near Chicago" land on those 3,134 rather than on the whole
   * catalogue. Going through `select` rather than writing to the filters
   * directly is deliberate: it is the combobox that owns the input text, the
   * clear button and the `combobox:change` the list listens on.
   *
   * The item is built here instead of being looked up in the box, because the
   * facets that fill it are a second round trip and the shopper can arrive
   * before it lands. An id that the facets later confirm survives their
   * rebuild; one they drop is cleared by `setItems`, which is the right
   * outcome either way.
   *
   * @param {string} id - Destination id.
   * @param {string} label - Its name, for the input.
   */
  applyDestinationFilter(id, label) {
    const box = this.experiencesList?.destinationBox;
    if (!box || !id) return;
    const known = box.items?.find((item) => String(item.id) === String(id));
    box.select(known || { id: String(id), label: label || '', sub: '' });
  }

  /**
   * How long the destination search will wait for the facets round trip that
   * fills it before opening anyway. Only reached when the warm-up in
   * `snippets/experiences-prefetch.liquid` missed and the request is genuinely
   * slow; an empty list the shopper can close beats a link that does nothing.
   */
  static FACETS_FOCUS_TIMEOUT = 2000;

  /**
   * Puts the cursor in the destination search, once the smooth scroll started
   * by `scrollToExperiences` has finished AND the destinations that fill it
   * have arrived.
   *
   * Waiting for the scroll matters: focusing mid-flight makes the browser
   * fight its own scroll, and on a phone the keyboard would come up over a
   * list that is still moving. `scrollend` is the exact signal, with a timer
   * behind it because it never fires when the scroll was a no-op (the list was
   * already at the top of the viewport) and does not fire at all in a
   * background or occluded tab, where rendering — and therefore scrolling — is
   * frozen.
   *
   * Waiting for the facets matters because this link is the one place the
   * dropdown is opened FOR the shopper rather than by them: `/eligible/filters`
   * is a separate round trip, and opening ahead of it showed an empty list
   * reading "Type to search 0 destinations". `setItems` re-renders an open
   * list, so it did eventually fill itself — this only stops the empty frame
   * being shown in the first place. Both waits are capped, so a stalled scroll
   * or a failed request still hands over focus.
   *
   * `preventScroll` because the scroll above owns the movement; the combobox's
   * own `focus` listener opens the suggestions.
   */
  focusDestinationSearch() {
    const list = this.experiencesList;
    const input = list?.destinationBox?.input;
    if (!input) return;

    const scrolled = new Promise((resolve) => {
      const settle = () => {
        window.removeEventListener('scrollend', settle);
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(settle, 900);
      window.addEventListener('scrollend', settle, { once: true });
    });

    // A rejected facets call is a resolved wait: loadFacets already falls back
    // to the map bubbles, and there is nothing further to wait for either way.
    const listed = Promise.race([
      Promise.resolve(list.facetsPromise).catch(() => {}),
      new Promise((resolve) => {
        setTimeout(
          resolve,
          SectionProductExploreExperiences.FACETS_FOCUS_TIMEOUT,
        );
      }),
    ]);

    Promise.all([scrolled, listed]).then(() => {
      input.focus({ preventScroll: true });
      // "Change city" arrives with the near state's city already in the box,
      // which narrows the suggestions to the one city the shopper is trying to
      // get away from. Selecting the text means the first letter they type
      // replaces it, so the list opens up again instead of having to be
      // cleared first. A no-op for "Pick their city", where the box is empty.
      input.select();
    });
  }

  /**
   * Opens the modal on the first row of the list — the top curated pick when
   * the SKU has one. No longer bound to the gift block (that scrolls to the
   * list now); kept for a caller that wants the modal straight from a button.
   */
  async openFirstExperience(button) {
    // The strip is clickable from first paint, so an early tap has to wait for
    // the calls rather than quietly do nothing. `_loadPromise` is undefined
    // only on the branch that already hid the strip.
    if (this._loadPromise) {
      button?.toggleAttribute('data-experiences-jump-busy', true);
      try {
        await this._loadPromise;
      } finally {
        button?.toggleAttribute('data-experiences-jump-busy', false);
      }
    }

    const list = this.experiencesList;
    if (!list?.experiences?.length) return;

    list.openExperienceDetails(
      ExperiencesList.prepare(list.experiences[0])._experience,
    );
  }

  async reload(sku) {
    if (!sku || sku === this.currentSku) return;
    const baseUrl = this.container.dataset.apiBaseUrl;
    if (!baseUrl) return;

    // A first variant without a SKU leaves the section unbuilt — no carousels,
    // no listeners — so the first usable SKU takes the full setup path instead
    // of refreshing lists that do not exist yet. A variant change that lands
    // while the initial load is still in flight must not start a second full
    // setup: two initLists() runs leave "load more" bound to the stale-SKU
    // instance. Wait the first one out and take the refresh path instead.
    if (!this.listsInitialized) {
      if (this._loadPromise) {
        await this._loadPromise;
      } else {
        await this.loadForSku(baseUrl, sku);
        return;
      }
    }

    this.currentSku = sku;
    this.productPath = `${baseUrl}/products/${encodeURIComponent(sku)}`;
    this.setThumbsLoading(true);

    // The map instance survives a variant change; only its data is swapped.
    // Its curated ring belongs to the old SKU, so that is refetched too — but
    // only when the map has actually booted; otherwise it waits for the boot.
    this._curatedPromise = null;
    this.curated = [];
    if (this.mapInstance) {
      this.mapInstance.productPath = this.productPath;
      this.mapInstance.setCurated([]);
      this.mapInstance.flyHome();
      if (this.mapInstance.panel) {
        this.mapInstance.panel.close();
        this.mapInstance.panel.productPath = this.productPath;
      }
    }

    const generation = ++this._loadGeneration;
    const [experiences, mapPoints] = await Promise.all([
      this.getExperiences(),
      this.loadMapPoints(),
      ...(this.mapInstance ? [this.loadCurated()] : []),
    ]);
    // Another variant change overtook this one. Its own load owns the section
    // now, so this answer is dropped rather than rendered over the top of it.
    if (generation !== this._loadGeneration) return;

    this.applyLoadedData(experiences, mapPoints);

    // Ahead of refreshLists: Swiper measures 0 inside a hidden section.
    this.updateSectionVisibility();
    this.refreshLists();
    SectionProductExploreExperiences.updateExperiencesPrices();
  }

  loadForSku(baseUrl, sku) {
    // Memoized so reload() can wait out a load still in flight rather than
    // racing a second one.
    this._loadPromise = this._loadForSku(baseUrl, sku);
    return this._loadPromise;
  }

  async _loadForSku(baseUrl, sku) {
    this.currentSku = sku;
    this.productPath = `${baseUrl}/products/${encodeURIComponent(sku)}`;

    // Start the map immediately rather than blocking it on /experiences (a
    // list-view payload), which delayed first paint.
    this.bindEvents();
    this.observeMapVisibility();

    const generation = ++this._loadGeneration;
    const experiencesRequest = this.getExperiences();
    // The strip above Add to cart is drawn from /experiences alone, so it does
    // not wait for /map-points, which snippets/experiences-prefetch.liquid holds
    // back until the opening gallery photo has loaded. A tap on a tile waits out
    // the whole load (`openExperienceFromThumb`), so painting early is safe.
    experiencesRequest.then((experiences) => {
      if (generation !== this._loadGeneration || !experiences.items.length)
        return;
      this.experiences = experiences.items;
      this.syncJumpButtons(true);
      this.setThumbsLoading(false);
      this.paintJumpThumbs();
      this._thumbsPaintedFor = experiences.items;
    });
    const [experiences, mapPoints] = await Promise.all([
      experiencesRequest,
      this.loadMapPoints(),
    ]);
    // A variant change landed while the first load was in flight; `reload`
    // waits this promise out and then refreshes, so the section is its to
    // build and this answer is the stale one.
    if (generation !== this._loadGeneration) return;

    this.applyLoadedData(experiences, mapPoints);

    // Ahead of initLists: Swiper measures 0 inside a hidden section.
    this.updateSectionVisibility();
    this.initLists();
    SectionProductExploreExperiences.updateExperiencesPrices();
  }

  observeMapVisibility() {
    if (!this.mapRoot || this._mapObserver) return;

    this._mapObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting))
          this.ensureMapInitialized();
      },
      { rootMargin: '200px' },
    );
    this._mapObserver.observe(this.mapRoot);
  }

  ensureMapInitialized() {
    if (!this.mapRoot || this.mapInstance)
      return this._mapInitPromise || Promise.resolve(this.mapInstance);

    if (!this._mapInitPromise) {
      // Ahead of the library download rather than after it: /curated is the
      // slowest call the section makes (seconds on a large gift), and until
      // it lands the ring is missing and the destination panel badges
      // nothing. Overlapping the two usually hides it entirely.
      this.loadCurated();

      this._mapInitPromise = loadMapboxGl()
        .then(() => {
          if (this.mapInstance) return this.mapInstance;

          this.mapInstance = this.initMap();
          // /map-points is already loaded (or in flight) from page load for the
          // stats line; seed the map from it instead of refetching. A request
          // still in flight lands on the instance itself once it resolves.
          if (this.mapInstance && this.mapPoints) {
            this.mapInstance.setDestinations(this.mapPoints);
          }

          return this.mapInstance;
        })
        .catch((error) => {
          console.error('Failed to load Mapbox GL:', error);
          this._mapInitPromise = null;
          return null;
        });
    }

    return this._mapInitPromise;
  }

  refreshLists() {
    if (!this.experiencesList) return;
    // A variant change is a new SKU: the list re-points at it and its facets
    // (destinations, count) are stale. A filter the shopper has set stays set
    // — they chose it, the gift option changed under it — so it is re-applied
    // against the new SKU rather than dropped with a fresh unfiltered page.
    this.experiencesList.setProductPath(this.productPath);
    if (this.experiencesList.hasActiveFilters()) {
      this.experiencesList.applyFilters();
    } else {
      this.experiencesList.init(this.experiencesResponse);
    }
  }

  initMap() {
    if (!this.mapRoot) return null;
    const instance = new ExploreExperiencesMap(this.mapRoot);
    instance.init(this.productPath);
    // /curated is fetched by loadCurated once the map boots; anything already
    // held (a reload that beat the boot) is applied here.
    instance.setCurated(this.curated);
    return instance;
  }

  refreshMapSize() {
    if (this.mapInstance?.map) {
      requestAnimationFrame(() => this.mapInstance.map.resize());
    }
  }

  initLists() {
    const wrapper = this.container.querySelector('[data-experiences-list]');

    if (wrapper) {
      this.experiencesList = new ProductExperiencesList(wrapper, {
        productPath: this.productPath,
        loadMoreSelector: '[data-load-more-experiences]',
        // The facets fallback for an API without /eligible/filters — see
        // ProductExperiencesList.loadFacets.
        getMapPoints: () => this.mapPoints || [],
      });
      this.experiencesList.init(this.experiencesResponse);
    }

    this.listsInitialized = true;
  }

  /**
   * Page 1 of the unified list: every experience the gift covers, the SKU's
   * curated picks first and each row flagged `curated`. Kept as the raw
   * response as well, because the list needs the cursor and page size.
   */
  async getExperiences() {
    try {
      const response = await this.fetchJson(
        ProductExperiencesList.experiencesUrl(this.productPath),
      );
      return { response, items: ProductExperiencesList.itemsFrom(response) };
    } catch (error) {
      console.error('Failed to load experiences:', error);
      return { response: null, items: [] };
    }
  }

  /**
   * The one place a finished load writes its answer onto the section, so the
   * generation check in front of it covers every field at once.
   *
   * @param {{response: (object|null), items: object[]}} experiences
   * @param {object[]} mapPoints
   */
  applyLoadedData(experiences, mapPoints) {
    this.experiencesResponse = experiences.response;
    this.experiences = experiences.items;
    this.mapPoints = mapPoints;
    this.mapInstance?.setDestinations(mapPoints);
    // The counts the near state needs live in these bubbles. Whichever of the
    // two answers last — this or the IP lookup — is the one that gets to
    // upgrade the line, so both call it. A variant change lands here too, with
    // new counts for the same city.
    this.upgradeLocationLine();
  }

  async getCurated() {
    try {
      const data = await this.fetchJson(`${this.productPath}/curated`);
      return Array.isArray(data)
        ? data.map((item) => ({ ...item, type: 'curated', curated: true }))
        : [];
    } catch (error) {
      console.error('Failed to load curated experiences:', error);
      return [];
    }
  }

  /**
   * The map's orange ring marks destinations holding a curated pick, and it
   * needs every curated row of the catalogue, not the ones the list happens
   * to have paged in — so the map alone still reads `/curated`. Asked for
   * when the map boots (ensureMapInitialized) rather than with the page: a
   * product that opens on the list never makes the call unless the shopper
   * switches to the map. Memoised per SKU; `reload` resets it.
   */
  loadCurated() {
    if (!this.mapRoot) return Promise.resolve([]);
    if (!this._curatedPromise) {
      const promise = this.getCurated().then((curated) => {
        // A variant change landed while this was in flight.
        if (this._curatedPromise !== promise) return curated;
        this.curated = curated;
        this.mapInstance?.setCurated(curated);
        return curated;
      });
      this._curatedPromise = promise;
    }
    return this._curatedPromise;
  }

  /**
   * /map-points feeds both the map bubbles and the catalogue-size line above
   * the toggle, so it is fetched even when the map is never opened. The
   * caller keeps the result (`applyLoadedData`) so a map booted later can be
   * seeded without a second request.
   */
  async loadMapPoints() {
    try {
      const data = await this.fetchJson(`${this.productPath}/map-points`);
      return Array.isArray(data?.content)
        ? data.content
        : Array.isArray(data)
        ? data
        : [];
    } catch (error) {
      console.error('Failed to load map points:', error);
      return [];
    }
  }

  fetchJson(url) {
    return fetchJson(url);
  }

  bindEvents() {
    if (this.eventsBound) return;
    this.eventsBound = true;

    $(this.container)
      .find('.switch_view')
      .on('click', (e) => this.switchView($(e.currentTarget).data('view')));
  }

  switchView(view) {
    this.view = view;
    const isMap = view === 'map';

    $(this.container).find('.map_view').toggleClass('hidden', !isMap);
    $(this.container).find('.list_view').toggleClass('hidden', isMap);

    $(this.container)
      .find('.switch_view')
      .each((_, btn) => {
        const isActive = $(btn).data('view') === view;
        $(btn)
          .toggleClass('bg-blue-4 text-white', isActive)
          .toggleClass('bg-neutral-1 text-blue-4', !isActive);
      });

    if (isMap) {
      this.ensureMapInitialized()?.then(() => this.refreshMapSize());
    }
  }

  static updateExperiencesPrices() {
    // Gift cards still submit their amount through a checked radio; every other
    // product now renders one fixed gift option as a hidden input, so there is
    // nothing `:checked` to find — fall back to whatever the form submits.
    const selected =
      document.querySelector('input[name="id"]:checked') ||
      document.querySelector('input[name="id"]');
    if (!selected) return;

    const label = selected.closest('[data-variant-price]');
    if (!label) return;

    const price = label.dataset.variantPrice || '';
    const compareAtPrice = label.dataset.variantCompareAtPrice || '';
    const saveElement = label.querySelector('[data-variant-save-text]');
    const saveHtml = saveElement ? saveElement.innerHTML.trim() : '';

    document.querySelectorAll('[data-variant-price-replace]').forEach((el) => {
      el.textContent = price;
    });

    document
      .querySelectorAll('[data-variant-compare-at-price-replace]')
      .forEach((el) => {
        el.textContent = compareAtPrice;
        el.classList.toggle('hidden', !compareAtPrice);
      });

    document
      .querySelectorAll('[data-variant-save-text-replace]')
      .forEach((el) => {
        el.innerHTML = saveHtml;
        el.classList.toggle('hidden', !saveHtml);
      });
  }
}

/**
 * Shared card rendering and modal wiring for anything that draws experience
 * cards from the API: `prepare` normalises a row, `populateCard` fills a clone
 * of `product-card-template`, and `openExperienceDetails` hands one card to the
 * details modal. `ProductExperiencesList` below is the list itself.
 */
class ExperiencesList {
  constructor(wrapper, { experiences = [], productPath = null }) {
    this.wrapper = wrapper;
    this.experiences = experiences;
    this.productPath = productPath;
    this.templateEl = document.querySelector('[data-product-card-template]');
  }

  /* What a card is keyed by across renders. Kept as its own function because two places have to
   * agree on it exactly: the id written onto the rendered element, and the id a later render
   * compares against to decide the element can be kept. */
  static idOf(item) {
    return item?.productCode || item?.destinationId || '';
  }

  static prepare(item) {
    const coordinates = item.location?.coordinates || item.coordinates || {};
    const location = item.location || {};
    const rating = item.combinedAverageRating || item.rating || 0;
    const reviews = item.totalReviews || item.reviews || 0;
    const maxPartySize = ExperiencesList.partySizeOf(item);

    return {
      url: item.productUrl || '#',
      featured_image: item.coverImageUrl,
      title: item.title || item.name || '',
      rating,
      review_count: reviews,
      reviews_text: ExperiencesList.formatReviews(reviews),
      stars_html: ExperiencesList.buildStars(rating),
      is_first: false,
      // `curated` is the unified list's own flag; `type` is what the legacy
      // /curated rows are tagged with by getCurated.
      is_curated: Boolean(item.curated || item.type === 'curated'),
      // The category line above the title. The server picks `mainCategory`
      // (always `categories[0]`) and leaves the key out when the row has no
      // category; the theme never picks. Aliased to the handout's names
      // (`category_l1` / `category_l1_all`) so assets/ew-category.js reads the
      // row as is.
      main_category: item.mainCategory || null,
      categories: Array.isArray(item.categories) ? item.categories : [],
      category_l1: item.mainCategory || null,
      category_l1_all: Array.isArray(item.categories) ? item.categories : [],
      location: ExperiencesList.formatLocation(location),
      // City and country only — what the card falls back to when the full
      // line does not fit; see `ProductExperiencesList.fitLocations`.
      location_short: ExperiencesList.formatLocation({
        city: location.city,
        country: location.country,
      }),
      max_party_size: maxPartySize,
      // The amount and party the "Fully covered" strip shows, or null — see
      // valueCover.
      value_cover: ExperiencesList.valueCover(item.value),
      _experience: {
        id: ExperiencesList.idOf(item),
        title: item.title || item.name || '',
        curated: Boolean(item.curated || item.type === 'curated'),
        // Carried to the modal: the detail endpoint knows nothing about the
        // gift, so the list row is the only place these come from.
        max_party_size: maxPartySize,
        value: item.value || null,
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        rating,
        reviews_amount: reviews,
        image: item.coverImageUrl,
        images: item.images || [],
        description:
          item.description || item.shortDescription || item.summary || '',
        location_name:
          location.name ||
          location.address ||
          item.name ||
          item.title ||
          [location.city, location.country].filter(Boolean).join(', ') ||
          '',
      },
    };
  }

  /**
   * The gift's price in minor units, read off the selected gift option
   * (`data-variant-price-cents` on the option's label / card). The sibling
   * `data-variant-price` is a formatted money string and cannot be parsed for
   * arithmetic, hence the separate attribute. `null` when nothing carries it.
   *
   * @returns {number|null}
   */
  static giftPriceCents() {
    const selected =
      document.querySelector('input[name="id"]:checked') ||
      document.querySelector('input[name="id"]');
    const source = selected?.closest('[data-variant-price-cents]');
    const cents = Number(source?.dataset.variantPriceCents);
    return Number.isInteger(cents) && cents > 0 ? cents : null;
  }

  /**
   * A whole-unit amount in the presentment currency, formatted the way every
   * other price on the storefront is, minus the zero fraction: "Worth $210",
   * not "Worth $210.00". `money_without_trailing_zeros` for the client.
   *
   * @param {number} amount - Whole units, never minor units.
   * @returns {string}
   */
  static formatWholeMoney(amount) {
    const formatted = window.SearchEngine?.formatMoney?.(amount);
    if (formatted) return formatted.replace(/[.,]00(?!\d)/, '');
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.theme?.currencyCode || 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  }

  /**
   * What the "Fully covered" strip adds for a row the API priced — the
   * whole-unit amount ("$226") and the party it is priced for ("for two", ''
   * for one person) — or null, and the strip says "Fully covered" alone.
   *
   * The backend decides who qualifies: `value` is only on rows whose Viator
   * list price is within its configured percentage of the gift price (which
   * may sit below the gift price), and it is absent everywhere else. The theme
   * renders, it does not decide, so no threshold is re-applied here — moving
   * the bar is a backend change alone.
   *
   * @param {{ listedPriceUsd?: string, ratio?: number, partySize?: number }} [value]
   * @returns {{ amount: string, party: string, units: number } | null}
   */
  static valueCover(value) {
    if (!value) return null;
    const partySize = Number(value.partySize);
    if (!Number.isInteger(partySize) || partySize < 1) return null;
    const amount = ExperiencesList.valueAmount(value);
    if (amount === null) return null;

    return {
      amount: ExperiencesList.formatWholeMoney(amount),
      party: ExperiencesList.formatCoverParty(partySize),
      units: amount,
    };
  }

  /**
   * The party on the "Fully covered" strip's first line: "for two", "for 12
   * people", or '' for one person. A lookup, not arithmetic: the party is
   * bounded by the box's range.
   *
   * @param {number} partySize - A positive whole number.
   * @returns {string}
   */
  static formatCoverParty(partySize) {
    return partySize > 1
      ? exploreString(`value_party_${partySize}`) ??
          exploreStringWith('value_party_other', { count: partySize })
      : '';
  }

  /**
   * What one row's "Fully covered" strip says. A row the API priced carries
   * its own party with the amount; every other row reads "Fully covered",
   * plus "for four" when the gift covers more than one person (`maxPartySize`,
   * the largest party whose cheapest tier fits the budget).
   *
   * @param {object|null} value - The row's `value`.
   * @param {number} partySize - The row's `maxPartySize`, 0 when unknown.
   * @returns {{ amount: string, party: string }}
   */
  static coverFor(value, partySize) {
    const priced = ExperiencesList.valueCover(value);
    return (
      priced || {
        amount: '',
        party: ExperiencesList.formatCoverParty(partySize),
      }
    );
  }

  /**
   * Fills one `experience-cover` strip: the party on line one, "Paid by your
   * gift" as line two and the amount in the panel for a priced row; line two
   * stays as an empty line otherwise, so every strip is the same height.
   *
   * The amount's whole units go on the figure as `data-units`, which page B's count-up
   * (assets/pdp-b-motion.js) formats frame by frame with `formatWholeMoney`.
   *
   * @param {Element|null} cover - The `.js-card-cover` element.
   * @param {{ amount: string, party: string, units?: number }} content
   */
  static fillCover(cover, { amount, party, units }) {
    if (!cover) return;
    cover.querySelector('.js-card-cover-title').textContent = party
      ? exploreStringWith('cover_title_party', { party })
      : exploreString('cover_title');
    cover.querySelector('.js-card-cover-line').textContent = amount
      ? exploreString('cover_line')
      : '';
    const figure = cover.querySelector('.js-card-cover-amount');
    figure.textContent = amount;
    if (amount && Number.isFinite(units)) figure.dataset.units = String(units);
    else delete figure.dataset.units;
    cover
      .querySelector('.js-card-cover-price')
      .classList.toggle('hidden', !amount);
  }

  /**
   * USD prints `listedPriceUsd` itself so a stale gift price mid-sale cannot
   * move it. Round to minor units BEFORE flooring: 19900 × 1.005025 is
   * 19999.9975, and flooring that straight prints $199 for a $200 listing.
   */
  static valueAmount(value) {
    if ((window.theme?.currencyCode || 'USD') === 'USD') {
      const listed = parseFloat(value.listedPriceUsd);
      return Number.isFinite(listed) ? Math.floor(listed) : null;
    }
    const ratio = Number(value.ratio);
    const giftPriceCents = ExperiencesList.giftPriceCents();
    if (!Number.isFinite(ratio) || ratio <= 0 || !giftPriceCents) return null;
    return Math.floor(Math.round(giftPriceCents * ratio) / 100);
  }

  static formatReviews(count) {
    const num = Number(count) || 0;
    const label = exploreString(num === 1 ? 'review_one' : 'review_other');
    return `(${formatNumber(num)} ${label})`;
  }

  /**
   * Where an experience is, as one line: "Fort Lauderdale, Florida, USA".
   *
   * City, region and country in that order and no other fields — `name` and
   * `address` are the venue, which on this endpoint is either absent or a
   * street line nobody scanning a grid of cards wants. Parts are dropped when
   * missing rather than left as empty commas, so a row with only a country
   * still reads.
   *
   * @param {object} [location] - The API row's `location` object.
   * @returns {string} The line, or '' when there is nothing to say.
   */
  static formatLocation(location) {
    return [location?.city, location?.region, location?.country]
      .filter(Boolean)
      .join(', ');
  }

  /**
   * How many people the gift covers for this experience, from the unified
   * list's `maxPartySize` (1…20): the largest party whose cheapest tier fits
   * the gift's budget. Affordability, not booking capacity — it ignores the
   * experience's own traveler cap and the gift's party range, and a smaller
   * size can still be unaffordable under group-tiered pricing — so the copy
   * stays "up to N", never "for N" or "1–N".
   *
   * Absent (not null) when the row's pricing tiers went missing between
   * ingests; that is null here and renders nothing, never `1`. Only
   * `/products/{sku}/experiences` carries the field — `/curated` and
   * `/eligible` rows never do, so the destination panel's cards stay silent.
   *
   * @param {object} [item] - An API row.
   * @returns {number|null}
   */
  static partySizeOf(item) {
    const size = Number(item?.maxPartySize);
    return Number.isInteger(size) && size > 0 ? size : null;
  }

  /**
   * @param {number} size - A positive party size from `partySizeOf`.
   * @returns {string} "Up to 4 people", or "For 1 person".
   */
  static formatPartySize(size) {
    return size === 1
      ? exploreString('party_up_to_one')
      : exploreStringWith('party_up_to_other', { count: size });
  }

  static buildStars(rating) {
    const full = Math.floor(rating || 0);
    const half = (rating || 0) - full;
    const nextStar = full + 1;
    const path =
      'M7.99992 11.8466L12.1199 14.3333L11.0266 9.64663L14.6666 6.49329L9.87325 6.08663L7.99992 1.66663L6.12659 6.08663L1.33325 6.49329L4.97325 9.64663L3.87992 14.3333L7.99992 11.8466Z';
    let html = '';

    for (let i = 1; i <= 5; i += 1) {
      if (i <= full) {
        html += `<svg width="16" height="16" viewBox="0 0 16 16" class="h-4 w-4 overflow-visible" fill="currentColor" role="img" aria-label="Full Star"><path d="${path}"/></svg>`;
      } else if (i === nextStar && half > 0) {
        html += `<svg width="16" height="16" viewBox="0 0 16 16" class="h-4 w-4 overflow-visible" role="img" aria-label="Half Star"><defs><clipPath id="list-half-clip-${i}"><rect x="0" y="0" width="${
          half * 16
        }" height="16"/></clipPath></defs><path d="${path}" fill="currentColor" opacity="0.25"/><path d="${path}" fill="currentColor" clip-path="url(#list-half-clip-${i})"/></svg>`;
      } else {
        html += `<svg width="16" height="16" viewBox="0 0 16 16" class="h-4 w-4 overflow-visible" fill="currentColor" role="img" aria-label="Empty Star" opacity="0.25"><path d="${path}"/></svg>`;
      }
    }
    return html;
  }

  /**
   * @param {object} experience - The `_experience` payload for the card. The
   *   modal opens on this summary straight away and fills the detail in as
   *   it lands.
   */
  openExperienceDetails(experience) {
    const modal = this.ensureModal();
    if (!modal || !experience?.id || !this.productPath) return;

    modal.open(experience, (item) => this.loadExperienceDetail(item));
  }

  /** One experience's full detail, in the shape the modal populates from. */
  async loadExperienceDetail(item) {
    const data = await fetchJson(
      `${this.productPath}/experience/${encodeURIComponent(item.id)}`,
    );
    return ExperienceModal.mapDetail(data, item);
  }

  ensureModal() {
    const modalEl = this.wrapper
      ?.closest('.section-explore-experiences')
      ?.querySelector('[data-experience-details-modal]');
    if (!modalEl) return null;

    if (!modalEl._experienceModal) {
      // Token/style/label hang off the section element, not the map root —
      // the modal is reachable from the list alone, with no map rendered.
      const config = this.wrapper?.closest('.explore_experiences')?.dataset;
      modalEl._experienceModal = new ExperienceModal(modalEl, {
        mapboxToken: config?.mapboxToken,
        mapboxStyle: config?.mapboxStyle,
        reviewsLabel: config?.reviewsLabel,
      });
    }

    return modalEl._experienceModal;
  }

  populateCard(card) {
    const url = card.dataset.url || '#';
    const featuredImage = card.dataset.featuredImage;
    const title = card.dataset.title || '';
    const rating = parseFloat(card.dataset.rating) || 0;
    const reviewCount = Number(card.dataset.reviewCount) || 0;
    const isFirst = card.dataset.isFirst === 'true';

    const link = card.querySelector('a');
    if (link) link.href = url;

    const image = card.querySelector('.js-card-image');
    if (image) {
      if (featuredImage) {
        image.src = featuredImage;
        image.alt = title;
      } else {
        image.classList.add('hidden');
      }
    }

    const titleEl = card.querySelector('.js-card-title');
    if (titleEl) titleEl.textContent = title;

    const ratingEl = card.querySelector('.js-card-rating');
    if (ratingEl) ratingEl.textContent = rating.toFixed(1);

    const reviewsTextEl = card.querySelector('.js-card-reviews-text');
    if (reviewsTextEl) {
      reviewsTextEl.textContent = ExperiencesList.formatReviews(reviewCount);
    }

    const starsEl = card.querySelector('.js-card-stars');
    if (starsEl) {
      starsEl.innerHTML = ExperiencesList.buildStars(rating);
    }

    // The location line under the title. The row holds its height either way —
    // see the comment in snippets/product-card-template.liquid — so this only
    // fills text and shows the pin; there is nothing to hide when it is empty.
    //
    // `removeAttribute`, not `.hidden = false`: the pin is an <svg>, and
    // `hidden` is defined on HTMLElement, so assigning it on an SVGElement sets
    // a property nothing reads and leaves the attribute in place.
    const location = card.dataset.location || '';
    const locationEl = card.querySelector('.js-card-location');
    if (locationEl) {
      locationEl.textContent = location;
      // Long lines are truncated in CSS, so give the pointer the whole thing.
      locationEl.title = location;
    }
    const pin = card.querySelector('[data-card-location-pin]');
    if (pin) {
      if (location) pin.removeAttribute('hidden');
      else pin.setAttribute('hidden', '');
    }

    // The category line sits above the title. A known category replaces the template's empty
    // line (replaced rather than filled because the line's ink is an inline custom property on
    // the element itself). No category, an unknown id or no assets/ew-category.js collapses it:
    // the line goes and so does the title's 6px gap under it, so the title sits under the
    // rating as it did before categories existed.
    const categoryEl = card.querySelector('.js-card-category');
    if (categoryEl) {
      let line = null;
      if (typeof window.ewCatLine === 'function') {
        const holder = document.createElement('template');
        holder.innerHTML = window.ewCatLine(card.dataset.mainCategory || '');
        line = holder.content.firstElementChild;
      }
      if (line && line.childElementCount) {
        line.classList.add('js-card-category');
        categoryEl.replaceWith(line);
      } else {
        categoryEl.remove();
        card.querySelector('.js-card-title')?.classList.remove('mt-1.5');
      }
    }

    // "Up to 4 people" on the row under the location, with the same two-figure
    // icon the cart summary puts on its party-size line. The row itself keeps
    // its height either way (the skeleton reserves it); only the span inside
    // toggles. The span is an HTMLElement, so `.hidden` works here.
    const partySize = Number(card.dataset.maxPartySize) || 0;
    const peopleEl = card.querySelector('.js-card-people');
    if (peopleEl) {
      peopleEl.hidden = !partySize;
      const peopleText = peopleEl.querySelector('.js-card-people-text');
      if (peopleText) {
        peopleText.textContent = partySize
          ? ExperiencesList.formatPartySize(partySize)
          : '';
      }
    }

    // The "Fully covered" strip on every card — see `coverFor`. The priced
    // amount and party were worked out once, into the data attributes.
    const amount = card.dataset.valueAmount || '';
    ExperiencesList.fillCover(card.querySelector('.js-card-cover'), {
      amount,
      party: amount
        ? card.dataset.valueParty || ''
        : ExperiencesList.formatCoverParty(partySize),
      units: amount ? Number(card.dataset.valueUnits) : undefined,
    });

    const reviewsRow = card.querySelector('.js-card-reviews');
    if (reviewsRow) {
      reviewsRow.classList.toggle('items-center', isFirst);
      reviewsRow.classList.toggle('flex-col', !isFirst);
    }

    const footer = card.querySelector('.js-card-footer');
    if (footer) {
      footer.classList.toggle('justify-between', isFirst);
      footer.classList.toggle('items-center', isFirst);
      footer.classList.toggle('flex-col', !isFirst);
    }
  }
}

/**
 * The experiences list: everything the gift covers, from
 * `GET /products/{sku}/experiences` — one keyset-paginated list with the
 * SKU's curated picks at the top and `curated` on every row. Curated rows are
 * a contiguous prefix under any filter, so once a page shows a row without the
 * flag no later row carries it.
 *
 * One grid at every width — a single scrolling column on a phone — grown a
 * page at a time by the Load more button.
 */
class ProductExperiencesList extends ExperiencesList {
  /**
   * What the storefront asks a page to be. Sixteen fills the 2 and 4 column
   * grids exactly and the PDP peek rail generously; it leaves the 3 column
   * grid one short on its last row, which twelve did not — that trade was
   * made deliberately. The reveal step is still read back off the response
   * rather than assumed — the endpoint clamps `size` to 1…50 and echoes what
   * it used. snippets/experiences-prefetch.liquid and the skeleton count in
   * snippets/product-experiences-list.liquid carry the same number.
   */
  static REQUESTED_PAGE_SIZE = 16;

  static DEFAULT_FILTERS = Object.freeze({
    numberOfPeople: 1,
    destinationId: '',
    // Category ids picked in the filter row; [] is All. Replaced, never
    // mutated (`ewKindToggle` returns a new array), so the frozen default is
    // safe to share.
    categories: Object.freeze([]),
  });

  /**
   * The `categories` parameter: the picked ids, sorted, comma-joined — or ''
   * for All, in which case the parameter is left out. Sorted because the API
   * caches publicly by URL, so one order per set means one cache entry.
   */
  static categoriesParam(filters) {
    const picked = Array.isArray(filters?.categories) ? filters.categories : [];
    return [...picked].sort().join(',');
  }

  /**
   * @param {string} [cursor] - Omitted for the first page.
   * @param {object} [filters] - `{ numberOfPeople, destinationId, categories }`; the
   *   parameters are only written when they differ from the defaults, so the
   *   unfiltered first-page URL stays byte-identical to the one
   *   snippets/experiences-prefetch.liquid warmed up.
   */
  static experiencesUrl(productPath, cursor, filters = null) {
    const params = new URLSearchParams({
      size: String(ProductExperiencesList.REQUESTED_PAGE_SIZE),
    });
    if (cursor) params.set('cursor', cursor);
    const people = Number(filters?.numberOfPeople) || 1;
    if (people > 1) params.set('numberOfPeople', String(people));
    if (filters?.destinationId) {
      params.set('destinationId', String(filters.destinationId));
      // A destination picked from the filter means "and everything under it"
      // — a country returns all of its cities. The map panel, which shares the
      // endpoint, deliberately does not send this: its drill-down must equal
      // the bubble count, which is leaf-only.
      params.set('destinationScope', 'all');
    }
    const categories = ProductExperiencesList.categoriesParam(filters);
    if (categories) params.set('categories', categories);
    return `${productPath}/experiences?${params}`;
  }

  /**
   * The rows of a page, each tagged for the card renderer. Tolerates a bare
   * array so a legacy-shaped answer still renders.
   */
  static itemsFrom(response) {
    const items = Array.isArray(response?.content)
      ? response.content
      : Array.isArray(response)
      ? response
      : [];
    return items.map((item) => ({
      ...item,
      curated: Boolean(item.curated),
      type: item.curated ? 'curated' : 'eligible',
    }));
  }

  constructor(
    wrapper,
    { productPath = null, loadMoreSelector = null, getMapPoints = null },
  ) {
    super(wrapper, { experiences: [], productPath });
    this.loadMoreSelector = loadMoreSelector;
    this.getMapPoints = getMapPoints;
    this.response = null;
    this.pageSize = ProductExperiencesList.REQUESTED_PAGE_SIZE;
    this.visibleCount = 0;
    this.isLoading = false;

    this.filters = { ...ProductExperiencesList.DEFAULT_FILTERS };
    this.facets = null;
    this.facetsPath = null;
    this.facetsPeople = 0;
    this.filterRequestId = 0;

    const section = wrapper?.closest('.explore_experiences');
    this.filterBar =
      section?.querySelector('[data-experiences-filters]') || null;
    const comboboxRoot = this.filterBar?.querySelector(
      '[data-destination-combobox]',
    );
    this.destinationBox = comboboxRoot
      ? comboboxRoot.combobox || new DestinationCombobox(comboboxRoot)
      : null;
    this.peopleSelect =
      this.filterBar?.querySelector(
        '[data-experiences-filter="numberOfPeople"]',
      ) || null;
    this.clearButton =
      this.filterBar?.querySelector('[data-experiences-filters-clear]') || null;
    this.countEl =
      this.filterBar?.querySelector('[data-experiences-count]') || null;
    this.progressEl =
      section?.querySelector('[data-experiences-progress]') || null;
    // The last count the API gave for the current SKU + filters, whichever
    // call carried it (the list's first page or /eligible/filters). null
    // means no honest number yet, and the progress line stays hidden.
    this.knownCount = null;
    // Set only from /eligible/filters, and only meaningful alongside a live
    // destination selection: updateCount renders it inside the destination
    // clause, so a selection the facets rebuild dropped takes the area with it.
    this.areaName = null;
    this.emptyEl = section?.querySelector('[data-experiences-empty]') || null;
    this.bodyEl = section?.querySelector('[data-experiences-body]') || null;

    // The category filter row. Present only while the section's
    // `show_category_filter` setting is on (off until the taxonomy covers 95%
    // of each gift's list); without it every category path below is a no-op
    // and `categories` is never sent. Drawn by assets/ew-category.js.
    this.kindsSlot = section?.querySelector('[data-experiences-kinds]') || null;
    this.kindsStatus =
      section?.querySelector('[data-experiences-kinds-status]') || null;
    this.kindsEmptyEl =
      section?.querySelector('[data-experiences-kinds-empty]') || null;
    // The facet's `{ id: count }` for the row, or null before /eligible/filters
    // answers (or with an API that does not send it yet).
    this.kindCounts = null;
    // "+N more" was tapped: every chip stays shown until the page reloads.
    this.kindsOpen = false;
    this.kindsDrawn = null;
    // The chip order the row first drew for this SKU (see `rowCounts`).
    this.kindsOrder = null;
    this.kindsOrderPath = null;
  }

  init(response) {
    this.response = response || null;
    // Reveal exactly what a request brought back, whatever that turns out to
    // be: a fixed reveal step meant a click could fetch a whole page and show
    // only part of it — or reveal a page it had not fetched.
    this.pageSize = Number(response?.size) || this.pageSize;
    const items = this.getItemsFromResponse(response);
    this.experiences = items;
    this.visibleCount = Math.min(this.pageSize, this.experiences.length);
    this.render();
    this.applyResponseCount(response);
    this.bindFilters();
    // Kept so the "Pick their city" jump can wait for the destinations before
    // it opens the dropdown, instead of opening an empty one.
    this.facetsPromise = this.loadFacets();
  }

  /**
   * The total the list endpoint now carries on its first page, so the count
   * lands with the cards instead of a round trip behind them.
   *
   * Absent means "no number in this payload", never zero: a "load more" page
   * omits it because the total cannot have changed, and the API omits it rather
   * than make the cards wait on a count that is expensive at large party sizes.
   * `loadFacets` still fills those in a moment later, exactly as before.
   *
   * @param {object|null} response - A page of the experiences list.
   * @param {'keep'|'clear'} [whenAbsent] - What an absent count means here.
   *   `keep` on first load, where there is nothing on screen to be wrong.
   *   `clear` after a filter change, where the number already showing counts
   *   the PREVIOUS filters: leaving it up states something false about the rows
   *   underneath it, and it is the mismatch this whole change set is about.
   */
  applyResponseCount(response, whenAbsent = 'keep') {
    const count = response?.count;
    if (count == null) {
      if (whenAbsent === 'clear') this.updateCount(null);
      return;
    }
    this.updateCount(Number(count));
  }

  /**
   * A variant change re-points the list; its facets belong to the old SKU.
   * Bumping the filter request id with them retires any `applyFilters` still
   * in flight for the old path — its rows would otherwise render into the new
   * SKU's list, since that guard only ever looked for a *newer* filter.
   */
  setProductPath(productPath) {
    this.productPath = productPath;
    this.facets = null;
    this.facetsPath = null;
    // The old SKU's total would otherwise sit under the new SKU's first page
    // when that page arrives without a `count`.
    this.knownCount = null;
    this.areaName = null;
    this.filterRequestId += 1;
  }

  hasActiveFilters() {
    return (
      Number(this.filters.numberOfPeople) > 1 ||
      Boolean(this.filters.destinationId) ||
      this.pickedKinds().length > 0
    );
  }

  pickedKinds() {
    return Array.isArray(this.filters.categories)
      ? this.filters.categories
      : [];
  }

  bindFilters() {
    if (!this.filterBar || this.filterBar.dataset.filtersBound) return;
    this.filterBar.dataset.filtersBound = 'true';

    const onChange = () => {
      this.filters = {
        numberOfPeople: Number(this.peopleSelect?.value) || 1,
        destinationId: this.destinationBox?.value || '',
        // Kept: the row is its own control, and a destination or party size
        // that leaves a picked category empty shows the empty state rather
        // than clearing the pick.
        categories: this.pickedKinds(),
      };
      this.applyFilters();
    };
    this.peopleSelect?.addEventListener('change', onChange);
    // The combobox's own event, not the input's native `change`: that one
    // fires on blur for any typed text, selected or not.
    this.destinationBox?.root.addEventListener('combobox:change', onChange);

    this.clearButton?.addEventListener('click', () => {
      this.filters = { ...ProductExperiencesList.DEFAULT_FILTERS };
      if (this.peopleSelect) {
        this.peopleSelect.value = '1';
        // The styled dropdown mirrors the native select only on `change`.
        this.peopleSelect.customSelect?.sync();
      }
      this.destinationBox?.clear(true);
      this.areaName = null;
      // The chips are filters too: "Clear filters" returns them to All.
      this.drawKinds();
      this.applyFilters();
    });

    this.bindKinds();
  }

  /**
   * The category row's clicks, "+N more", and the re-clamp on resize and once
   * the font has loaded (chip widths change with it). One delegated listener
   * on the slot, plus one on the empty state for its "Show all kinds" button.
   */
  bindKinds() {
    if (!this.kindsSlot || this.kindsSlot.dataset.kindsBound) return;
    if (typeof window.ewKindRow !== 'function') return;
    this.kindsSlot.dataset.kindsBound = 'true';

    const onClick = (event) => {
      const more = event.target.closest('.ew-kind--more');
      if (more) {
        const hidden =
          this.kindsSlot.querySelectorAll('.ew-kind[hidden]').length;
        this.kindsOpen = true;
        window.ewKindOpen(this.kindsSlot);
        this.kindsDrawn = null;
        ProductExperiencesList.track({
          event: 'ew_kind_more',
          ew_hidden: hidden,
          ew_page: 'product',
        });
        return;
      }
      const chip = event.target.closest('[data-kind]');
      if (chip) this.toggleKind(chip.getAttribute('data-kind'));
    };
    this.kindsSlot.addEventListener('click', onClick);
    this.kindsEmptyEl?.addEventListener('click', onClick);

    let wait = null;
    const clamp = () => window.ewKindClamp(this.kindsSlot, this.kindsOpen);
    window.addEventListener('resize', () => {
      clearTimeout(wait);
      wait = setTimeout(clamp, 100);
    });
    document.fonts?.ready.then(clamp);
  }

  /**
   * A chip tap (or "Show all kinds"): the row redraws at once with the new
   * pressed state, then the list starts again from page 1 exactly as a
   * destination change does — the keyset cursor is not bound to the
   * categories, so it must never carry across a change of the picked set.
   */
  toggleKind(id) {
    const before = this.pickedKinds();
    const picked = window.ewKindToggle(before, id);
    if (picked.join() === before.join()) return;

    this.filters = { ...this.filters, categories: picked };
    ProductExperiencesList.track({
      event: 'ew_kind_filter',
      ew_kind: id,
      ew_kind_action: id === 'all' ? 'all' : picked.length ? 'pick' : 'clear',
      ew_kinds_picked: ProductExperiencesList.categoriesParam(this.filters),
      ew_page: 'product',
    });
    this.drawKinds(id === 'all' ? null : id);
    this.applyFilters();
  }

  /**
   * Draws the category row from the facet counts and the picked set. The
   * facet ignores the picked categories, so after a tap it usually comes back
   * identical: an unchanged row is left alone rather than rebuilt, which keeps
   * the tapped chip focused and the swipe row where the shopper left it.
   *
   * @param {string|null} [tapped] - The chip just tapped, to focus and scroll
   *   into view.
   */
  drawKinds(tapped = null) {
    if (!this.kindsSlot || typeof window.ewKindRow !== 'function') return;
    const picked = this.pickedKinds();
    const counts = this.rowCounts();
    const key = JSON.stringify([counts, picked, this.kindsOpen]);
    if (key === this.kindsDrawn) {
      if (tapped) {
        window.ewKindPick(this.kindsSlot, picked, this.kindsStatus, tapped);
      }
      return;
    }
    this.kindsDrawn = key;
    // Until the facet answers, only a pick the shopper already made is drawn
    // (ewKindRow keeps picked ids even at no count); an empty row gives the
    // held space back.
    this.kindsSlot.innerHTML = window.ewKindRow(counts || {}, picked);
    window.ewKindClamp(this.kindsSlot, this.kindsOpen);
    window.ewKindPick(this.kindsSlot, picked, this.kindsStatus, tapped);
  }

  /**
   * The facet counts re-keyed so the chip order the row first drew for this
   * SKU holds for the page. Each backend task caches the facet on its own, so
   * two answers can disagree on a count (after a tag upload, say); sorting by
   * every answer reshuffled the chips after a tap or a reload. A category that
   * turns up later goes last, one that drops out leaves (a picked one stays:
   * `ewKindsIn` keeps it). The counts are never shown, only their order.
   *
   * @returns {object|null} `{ id: rank }`, or null before the facet answers.
   */
  rowCounts() {
    if (!this.kindCounts || typeof window.ewKindsIn !== 'function') {
      return this.kindCounts;
    }
    const fresh = window.ewKindsIn(this.kindCounts, []);
    const kept = (this.kindsOrder || []).filter((id) => fresh.includes(id));
    this.kindsOrder = kept.concat(fresh.filter((id) => !kept.includes(id)));
    const total = this.kindsOrder.length;
    return Object.fromEntries(this.kindsOrder.map((id, i) => [id, total - i]));
  }

  /** A dataLayer push that can never break the list. */
  static track(payload) {
    try {
      (window.dataLayer = window.dataLayer || []).push(payload);
    } catch (error) {
      // Tracking must never stand between the shopper and the list.
    }
  }

  setFiltering(isFiltering) {
    if (this.bodyEl) {
      this.bodyEl.setAttribute('aria-busy', isFiltering ? 'true' : 'false');
      this.bodyEl.classList.toggle('opacity-50', isFiltering);
      this.bodyEl.classList.toggle('pointer-events-none', isFiltering);
    }
    if (this.peopleSelect) {
      this.peopleSelect.disabled = isFiltering;
      const trigger = this.peopleSelect.customSelect?.trigger;
      if (trigger) trigger.disabled = isFiltering;
    }
    // The combobox stays live: locking it while a page loads would swallow
    // keystrokes and snap the text back mid-word. applyFilters already drops
    // any answer that a later selection has overtaken.
  }

  /**
   * Fetches the first page under the current filters and replaces the list.
   * A stale answer (the shopper changed a filter again while this one was in
   * flight) is dropped: only the latest request gets to render.
   *
   * The page and the facets are awaited together and the bar is written once,
   * in the same synchronous pass as the rows. Painting each as it arrived
   * showed a visible correction on every destination pick ("… in Oahu", then
   * "… in Oahu and Hawaii" from the facets, the only call that knows the
   * area), and a page without a `count` could blank a line the facets had
   * just written.
   */
  async applyFilters() {
    if (!this.productPath) return;
    const requestId = ++this.filterRequestId;
    const requestPath = this.productPath;
    const people = Number(this.filters.numberOfPeople) || 1;
    this.areaName = null;

    // `focusDestinationSearch` waits on this for a filled dropdown.
    let facetsApplied;
    this.facetsPromise = new Promise((resolve) => {
      facetsApplied = resolve;
    });

    this.setFiltering(true);
    try {
      const [page, facets] = await Promise.all([
        fetchJson(
          ProductExperiencesList.experiencesUrl(
            requestPath,
            null,
            this.filters,
          ),
        ).catch((error) => {
          console.error('Failed to filter experiences:', error);
          return null;
        }),
        this.fetchFacets(),
      ]);
      if (requestId !== this.filterRequestId) return;

      if (page) {
        this.response = page;
        this.pageSize = Number(page.size) || this.pageSize;
        this.experiences = this.getItemsFromResponse(page);
      } else {
        this.response = { ...(this.response || {}), hasNext: false };
        this.experiences = [];
      }
      this.visibleCount = Math.min(this.pageSize, this.experiences.length);
      this.render();
      this.applyFacets(facets, requestPath, people);
      if (page && this.knownCount == null) {
        this.applyResponseCount(page, 'clear');
      }
    } finally {
      if (requestId === this.filterRequestId) this.setFiltering(false);
      facetsApplied();
    }
  }

  /**
   * Fetches and applies the facets on their own — the first-load path, where
   * the rows arrived via `init`. A stale answer is dropped on the same rule
   * `applyFilters` uses, because this one writes the count.
   */
  async loadFacets() {
    if (!this.filterBar || !this.productPath) return;
    const requestId = this.filterRequestId;
    const requestPath = this.productPath;
    const people = Number(this.filters.numberOfPeople) || 1;

    const facets = await this.fetchFacets();
    if (
      requestPath !== this.productPath ||
      requestId !== this.filterRequestId
    ) {
      return;
    }
    this.applyFacets(facets, requestPath, people);
  }

  /**
   * The `/eligible/filters` payload for the current SKU and filters. Network
   * only, so a caller can hold it until everything else it paints is ready.
   *
   * @returns {Promise<object|null>} null for any failure; never rejects.
   */
  async fetchFacets() {
    if (!this.filterBar || !this.productPath) return null;
    const people = Number(this.filters.numberOfPeople) || 1;
    const params = new URLSearchParams({ numberOfPeople: String(people) });
    if (this.filters.destinationId) {
      params.set('destinationId', String(this.filters.destinationId));
    }
    // Only `count` depends on this — the categories and destinations facets
    // ignore the pick — but the count under the filters has to answer it.
    const categories = ProductExperiencesList.categoriesParam(this.filters);
    if (categories) params.set('categories', categories);
    try {
      const facets = await fetchJson(
        `${this.productPath}/eligible/filters?${params}`,
      );
      return facets && Array.isArray(facets.destinations) ? facets : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Writes the facets to the bar, synchronously. The destinations facet
   * depends on the party size only, so the dropdown is rebuilt when that
   * changes and left alone otherwise (a rebuild would drop the open list from
   * under the shopper); the count depends on both filters.
   *
   * With no facets (an API that does not serve them yet, or any failure) the
   * dropdown is filled from the `/map-points` bubbles instead and the count
   * is the bubbles' sum, or the picked bubble's own count.
   *
   * @param {object|null} facets - From `fetchFacets`.
   * @param {string} requestPath - The SKU path the facets were fetched for.
   * @param {number} people - The party size they were fetched for.
   */
  applyFacets(facets, requestPath, people) {
    if (!this.filterBar) return;
    if (facets) {
      this.facets = facets;
      if (this.facetsPath !== requestPath || this.facetsPeople !== people) {
        this.populateDestinations(facets.destinations);
        this.facetsPath = requestPath;
        this.facetsPeople = people;
      }
      const area = facets.destinationAreaName;
      this.areaName =
        typeof area === 'string' && area.trim() ? area.trim() : null;
      this.updateCount(Number(facets.count));
      if (this.kindsOrderPath !== requestPath) {
        this.kindsOrder = null;
        this.kindsOrderPath = requestPath;
      }
      // Already in the row's order (most experiences first, ties by name) and
      // without zero counts; absent before the backend sends it — no row.
      this.kindCounts = Array.isArray(facets.categories)
        ? Object.fromEntries(
            facets.categories
              .filter((option) => option && option.id)
              .map((option) => [option.id, Number(option.count) || 0]),
          )
        : null;
      this.drawKinds(this.focusedKind());
      return;
    }

    this.kindCounts = null;
    this.drawKinds(this.focusedKind());

    // Fallback: the map bubbles. The failed or rejected payload took the area
    // name with it, so the count must not keep claiming it.
    this.areaName = null;
    const points = (this.getMapPoints?.() || []).filter(
      (point) => point && point.destinationId != null && point.name,
    );
    if (this.facetsPath !== requestPath) {
      const byCountry = new Map();
      points.forEach((point) => {
        const country = point.country || '';
        if (!byCountry.has(country)) byCountry.set(country, []);
        byCountry
          .get(country)
          .push({ id: point.destinationId, name: point.name });
      });
      const countries = Array.from(byCountry.entries())
        .map(([name, cities]) => ({
          id: null,
          name,
          cities: cities.sort((a, b) => a.name.localeCompare(b.name)),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      this.populateDestinations(countries);
      this.facetsPath = requestPath;
      this.facetsPeople = people;
    }

    if (people > 1 || this.pickedKinds().length) {
      // The bubbles are counted at party size 1 and over every category; a
      // count would be a guess.
      this.updateCount(null);
    } else if (this.filters.destinationId) {
      const point = points.find(
        (candidate) =>
          String(candidate.destinationId) ===
          String(this.filters.destinationId),
      );
      this.updateCount(point ? Number(point.count) : null);
    } else {
      this.updateCount(
        points.reduce((sum, point) => sum + (Number(point.count) || 0), 0),
      );
    }
  }

  /**
   * @param {Array<{id: (number|string|null), name: string, cities: Array<{id: (number|string), name: string}>}>} countries
   *   Flattened for the type-ahead: a city becomes "Paris · France", a country
   *   with an id of its own becomes "France · Country" (it filters to
   *   everything in it). The current selection survives the rebuild when its
   *   id is still present, and is cleared — without firing a change — when it
   *   is not.
   */
  populateDestinations(countries) {
    if (!this.destinationBox) return;

    // The facets carry no volumes, but the map bubbles do, keyed by the same
    // destination id: the type-ahead uses them to rank its matches.
    const countById = new Map();
    (this.getMapPoints?.() || []).forEach((point) => {
      if (point?.destinationId != null) {
        countById.set(String(point.destinationId), Number(point.count) || 0);
      }
    });

    const items = [];
    countries.forEach((country) => {
      const countryName = country.name || '';
      if (country.id != null) {
        items.push({
          id: String(country.id),
          label: countryName,
          sub: exploreString('filter_country_label'),
          // A country's own name counts double, so "fra" lists France before
          // every French city.
          search: `${countryName} ${countryName}`,
          isCountry: true,
          count: countById.get(String(country.id)) || 0,
        });
      }
      (Array.isArray(country.cities) ? country.cities : []).forEach((city) => {
        items.push({
          id: String(city.id),
          label: city.name || '',
          sub: countryName,
          search: `${city.name || ''} ${countryName}`,
          isCountry: false,
          count: countById.get(String(city.id)) || 0,
        });
      });
    });

    const kept = this.destinationBox.setItems(items);
    if (!kept && this.filters.destinationId) {
      this.filters = { ...this.filters, destinationId: '' };
    }
  }

  selectedDestinationName() {
    return this.destinationBox?.selectedLabel || '';
  }

  /**
   * @param {number|null} count - null hides the line (nothing honest to say).
   *   The unfiltered total is hidden too: it repeats what the section already
   *   says above the list, and only earns its place once it answers a filter.
   */
  updateCount(count) {
    this.knownCount = count == null || Number.isNaN(count) ? null : count;
    this.updateProgress();
    if (!this.countEl) return;
    if (!this.hasActiveFilters() || count == null || Number.isNaN(count)) {
      this.countEl.textContent = '';
      return;
    }
    const people = Number(this.filters.numberOfPeople) || 1;
    const parts = [
      exploreStringWith(count === 1 ? 'count_one' : 'count_other', {
        count: formatNumber(count),
      }),
    ];
    if (people > 1) {
      parts.push(
        exploreStringWith('count_for_people', {
          people: exploreStringWith('filter_people_other', { count: people }),
        }),
      );
    }
    const destination = this.selectedDestinationName();
    if (destination) {
      parts.push(
        this.areaName
          ? exploreStringWith('count_in_destination_area', {
              destination,
              area: this.areaName,
            })
          : exploreStringWith('count_in_destination', { destination }),
      );
    }
    this.countEl.textContent = parts.join(' ');
  }

  /**
   * The merchant's headline total for the SKU on screen, from the variant's
   * `ew_pdp.experience_count` metafield — rendered by the section as a
   * SKU → total map in `data-experiences-totals`. Read at call time, not
   * captured: a variant change rewrites `data-product-sku` in place.
   *
   * @returns {number|null} null when the variant has no total set.
   */
  variantTotal() {
    const section = this.wrapper?.closest('.explore_experiences');
    const sku = section?.dataset.productSku;
    const raw = section?.dataset.experiencesTotals;
    if (!sku || !raw) return null;
    if (this.variantTotalsRaw !== raw) {
      this.variantTotalsRaw = raw;
      try {
        this.variantTotals = JSON.parse(raw);
      } catch (error) {
        this.variantTotals = null;
      }
    }
    const total = Number(this.variantTotals?.[sku]);
    return total > 0 ? total : null;
  }

  /**
   * The "of N" the progress line shows. The merchant's cap only stands for the
   * whole list: once a filter is set the API's count for that filter is the
   * only honest number. Never smaller than what is on screen, so a cap set
   * low never reads "Showing 112 of 100".
   *
   * @returns {number|null} null hides the line.
   */
  progressTotal() {
    const cap = this.hasActiveFilters() ? null : this.variantTotal();
    const total = cap ?? this.knownCount;
    if (total == null || Number.isNaN(total)) return null;
    return Math.max(total, this.visibleCount);
  }

  /**
   * "Showing 12 of 48,000+ experiences" under the cards, above Load more.
   * The `+` is only for the merchant's `ew_pdp.experience_count`, a rounded
   * headline rather than a tally; a filtered count is the API's exact number.
   */
  updateProgress() {
    if (!this.progressEl) return;
    const shown = this.visibleCount;
    const total = this.progressTotal();
    if (!shown || total == null) {
      this.progressEl.hidden = true;
      this.progressEl.textContent = '';
      return;
    }
    const complete = shown >= total && !this.hasMore();
    const fromMetafield =
      !this.hasActiveFilters() && this.variantTotal() != null;
    const totalLabel =
      fromMetafield && !complete
        ? exploreStringWith('see_all_count', { count: formatNumber(total) })
        : formatNumber(total);
    this.progressEl.textContent = complete
      ? exploreStringWith('progress_all', { total: totalLabel })
      : exploreStringWith('progress', {
          shown: formatNumber(shown),
          total: totalLabel,
        });
    this.progressEl.hidden = false;
  }

  updateFilterChrome() {
    const active = this.hasActiveFilters();
    if (this.clearButton) this.clearButton.hidden = !active;

    const empty = this.experiences.length === 0;
    // Picked categories with nothing left under the destination or party
    // size: the chips stay pressed and the handout's own empty state offers
    // "Show all kinds". With nothing picked, the list's usual empty state.
    const kindsEmpty =
      empty &&
      this.pickedKinds().length > 0 &&
      Boolean(this.kindsEmptyEl) &&
      typeof window.ewKindEmpty === 'function';
    if (this.kindsEmptyEl) {
      if (kindsEmpty && !this.kindsEmptyEl.firstElementChild) {
        this.kindsEmptyEl.innerHTML = window.ewKindEmpty();
      }
      this.kindsEmptyEl.hidden = !kindsEmpty;
    }
    if (this.emptyEl) this.emptyEl.hidden = !(empty && active) || kindsEmpty;
    if (this.bodyEl) this.bodyEl.classList.toggle('hidden', empty && active);
  }

  /** The chip that has focus, so a redraw can put it back; null otherwise. */
  focusedKind() {
    const active = document.activeElement;
    if (!this.kindsSlot || !active || !this.kindsSlot.contains(active)) {
      return null;
    }
    const kind = active.getAttribute('data-kind');
    return kind && kind !== 'all' ? kind : null;
  }

  getItemsFromResponse(response) {
    return ProductExperiencesList.itemsFrom(response);
  }

  render() {
    if (!this.templateEl) return;

    this.renderInto(this.wrapper, this.experiences.slice(0, this.visibleCount));

    this.bindLoadMore();
    this.updateFilterChrome();
    this.updateProgress();
    SectionProductExploreExperiences.updateExperiencesPrices();
  }

  /**
   * @param {HTMLElement} target - The grid.
   * @param {object[]} visible - The slice to show.
   */
  renderInto(target, visible) {
    /* Load more is additive, so the prefix that has not changed is kept as-is and only the tail
     * is built. Rebuilding the whole grid threw away every card on screen — new elements, new
     * `<img>`s, nothing decoded — so the cards the shopper was looking at blanked and came back.
     * Identity is the experience id written on each element; a filter change or a new SKU
     * returns different ids (or a shorter list) and falls straight back to a full rebuild. */
    const rendered = Array.from(target.children);
    const extending = ProductExperiencesList.isPrefixOf(rendered, visible);

    const added = visible
      .slice(extending ? rendered.length : 0)
      .map((item) => this.buildCard(item))
      .filter(Boolean);

    if (extending) target.append(...added);
    else target.replaceChildren(...added);

    ProductExperiencesList.fitLocations(added);
    // Page B's card motion (assets/pdp-b-motion.js) plays each card the first time it shows, so it
    // has to hear about cards that Load more or a filter brings in, not only the first page.
    target.dispatchEvent(
      new CustomEvent('ew:experience-cards', {
        bubbles: true,
        detail: { cards: added },
      }),
    );
  }

  /**
   * Drops the region from a location line that does not fit its card —
   * "Grand Teton National Park, Wyoming, USA" becomes "Grand Teton National
   * Park, USA" rather than "Grand Teton National Park, Wyo…". The region is
   * the least informative of the three parts once the city is a landmark, and
   * a cut place name is the one thing a gift buyer scanning the grid should
   * never have to guess at. Anything still too long ellipsises as before,
   * with the full line in the `title` `populateCard` set.
   *
   * Runs after the cards are in the grid: `populateCard` fills a detached
   * clone, where nothing has a width yet.
   *
   * @param {Element[]} cards - Rendered cards, already in the document.
   */
  static fitLocations(cards) {
    cards.forEach((card) => {
      const short = card.dataset.locationShort;
      const el = card.querySelector('.js-card-location');
      if (!el || !short || short === el.textContent) return;
      if (el.scrollWidth > el.clientWidth) el.textContent = short;
    });
  }

  /**
   * True when `rendered` is exactly the front of `visible`, card for card, and can therefore be
   * kept. An element with no id — the skeletons the section ships in the markup — never matches,
   * so the first real render always replaces them.
   */
  static isPrefixOf(rendered, visible) {
    if (rendered.length === 0 || rendered.length > visible.length) return false;

    return rendered.every(
      (el, index) =>
        el.dataset?.experienceId &&
        el.dataset.experienceId === ExperiencesList.idOf(visible[index]),
    );
  }

  /**
   * @returns {Element|null} The card to put in the grid. The shared template ships it wrapped in
   *   a `.swiper-slide` for the carousels elsewhere; the wrapper is dropped here, since a slide's
   *   sizing on a grid cell breaks the grid.
   */
  buildCard(item) {
    const data = ExperiencesList.prepare(item);
    const slide = this.templateEl.content.cloneNode(true).firstElementChild;
    const card = slide.querySelector('[data-url]');
    if (!card) return null;

    card.dataset.url = data.url;
    card.dataset.featuredImage = data.featured_image;
    card.dataset.title = data.title;
    card.dataset.rating = String(data.rating);
    card.dataset.reviewCount = String(data.review_count);
    card.dataset.isFirst = data.is_first ? 'true' : '';
    card.dataset.curated = data.is_curated ? 'true' : '';
    card.dataset.mainCategory = data.main_category || '';
    card.dataset.valueAmount = data.value_cover?.amount || '';
    card.dataset.valueParty = data.value_cover?.party || '';
    card.dataset.valueUnits = data.value_cover
      ? String(data.value_cover.units)
      : '';
    card.dataset.location = data.location;
    card.dataset.locationShort = data.location_short;
    card.dataset.maxPartySize = data.max_party_size
      ? String(data.max_party_size)
      : '';

    this.populateCard(card);

    const link = card.querySelector('a');
    if (link && data._experience?.id && this.productPath) {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.blur();
        this.trackCardClick(data);
        this.openExperienceDetails(data._experience);
      });
    }

    // Read back by `isPrefixOf` on the next render.
    card.dataset.experienceId = ExperiencesList.idOf(item);

    return card;
  }

  /**
   * `ew_card_click` for the category tracking hooks (FINAL Category Line and
   * Filter Row 2026-09-25): the card's main category, the experience's
   * `productCode` and the categories picked in the filter row when it was
   * clicked ('' for All). The GTM tags that read it are set up separately.
   */
  trackCardClick(data) {
    try {
      (window.dataLayer = window.dataLayer || []).push({
        event: 'ew_card_click',
        ew_kind: data.main_category || '',
        ew_experience: data._experience?.id || '',
        ew_kinds_picked: [...(this.filters?.categories || [])].sort().join(','),
        ew_page: 'product',
      });
    } catch (error) {
      // Tracking must never stand between the shopper and the details.
    }
  }

  async bindLoadMore() {
    if (!this.loadMoreSelector) return;

    const button = this.wrapper
      ?.closest('.explore_experiences')
      ?.querySelector(this.loadMoreSelector);
    if (!button) return;

    this.updateLoadMoreButton(button);

    if (button.dataset.loadMoreBound) return;
    button.dataset.loadMoreBound = 'true';

    button.addEventListener('click', () => this.loadMore(button));
  }

  hasMore() {
    return Boolean(
      this.response?.hasNext || this.visibleCount < this.experiences.length,
    );
  }

  /**
   * Reveals one more page — fetched if the list does not hold it yet — and
   * re-renders. `isLoading` keeps a double tap from stacking requests.
   *
   * @param {HTMLElement} [button] - Dimmed while the page is in flight.
   */
  async loadMore(button = null) {
    if (this.isLoading || !this.hasMore()) return;

    this.isLoading = true;
    button?.classList.add('opacity-50', 'cursor-wait');
    try {
      const targetCount = this.visibleCount + this.pageSize;
      // A restart has already replaced the list with page 1 and rendered it.
      // Revealing a count measured against the list as it was before would
      // render the same page a second time.
      if (await this.ensureItems(targetCount)) return;
      this.visibleCount = Math.min(targetCount, this.experiences.length);
      this.render();
    } catch (error) {
      console.error('Failed to load more experiences:', error);
    } finally {
      this.isLoading = false;
      button?.classList.remove('opacity-50', 'cursor-wait');
    }
  }

  updateLoadMoreButton(button) {
    // The attribute rather than the class: `[hidden]` is `!important` in
    // preflight, so it also wins against the display utilities on the button.
    button.hidden = !this.hasMore();
  }

  /**
   * @returns {Promise<boolean>} `true` when a refused cursor sent the list
   *   back to page 1, so the caller must not go on to reveal more of a list
   *   that no longer exists.
   */
  async ensureItems(targetCount) {
    while (
      this.experiences.length < targetCount &&
      this.response?.hasNext &&
      this.response?.nextCursor
    ) {
      const response = await fetch(
        ProductExperiencesList.experiencesUrl(
          this.productPath,
          this.response.nextCursor,
          this.filters,
        ),
      );
      // A refused cursor (the only 400 the endpoint returns: malformed, minted
      // for another product, or by a legacy endpoint) is not a failure to
      // retry — the position it pointed at is gone. The endpoint's own rule is
      // to start again from page 1 under the current filters, which is what
      // applyFilters does.
      if (response.status === 400) {
        await this.applyFilters();
        return true;
      }
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);

      const nextResponse = await response.json();
      // Re-read every page, not only the first: once the API honours `size` and
      // clamps or varies it, a reveal step frozen at the first page's value
      // drifts from what is actually being fetched — the mismatch this reads
      // the field to avoid.
      this.pageSize = Number(nextResponse?.size) || this.pageSize;
      const newItems = this.getItemsFromResponse(nextResponse);

      if (!newItems.length) {
        this.response = { ...nextResponse, hasNext: false };
        break;
      }

      this.response = nextResponse;
      this.experiences = this.experiences.concat(newItems);
    }

    return false;
  }
}

/**
 * Type-ahead destination picker for the eligible list's filter bar
 * (`[data-destination-combobox]` in snippets/product-experiences-list.liquid).
 *
 * The catalogue spans ~2,500 destinations, which is far past what a dropdown
 * can hold: a shopper who wants Las Vegas should type "las" and pick it, not
 * scroll through Albania. So this is a combobox in the WAI-ARIA sense — a text
 * input that filters a listbox as you type, with arrow/Enter/Escape keyboard
 * handling and `aria-activedescendant` for screen readers. The value it holds
 * is the destination *id*; the text is only ever a label for it.
 *
 * Items come from `setItems` as `{ id, label, sub, search, isCountry }`; with
 * nothing typed it lists the priority country with all of its cities, then
 * the other countries (a browseable few hundred), and from the first
 * character it ranks every item — exact and prefix matches first, then word
 * starts, then anything containing the text — capped at MAX_RESULTS.
 *
 * Fires `combobox:change` on its root when the selection changes; never on a
 * keystroke. Styles are `.combobox*` in app.scss, beside `.custom-select*`,
 * whose trigger it is drawn to match.
 */
class DestinationCombobox {
  static MAX_RESULTS = 60;
  static MAX_LIST_HEIGHT = 365;
  static MIN_LIST_HEIGHT = 160;
  static instances = 0;

  constructor(root) {
    this.root = root;
    this.input = root.querySelector('[data-combobox-input]');
    this.list = root.querySelector('[data-combobox-list]');
    this.clearButton = root.querySelector('[data-combobox-clear]');
    this.uid = `destination-combobox-${++DestinationCombobox.instances}`;

    this.items = [];
    this.results = [];
    this.activeIndex = -1;
    this.isOpen = false;
    this.selected = null;
    // Theme settings → Product page → "Country shown first". The catalogue
    // says "USA"; a merchant typing the long form still gets the same country.
    this.priorityCountry = DestinationCombobox.fold(
      root.dataset.priorityCountry,
    );
    root.combobox = this;

    if (!this.input || !this.list) return;
    if (!this.list.id) this.list.id = `${this.uid}-list`;
    this.input.setAttribute('aria-controls', this.list.id);
    this.bindEvents();
  }

  get value() {
    return this.selected ? this.selected.id : '';
  }

  get selectedLabel() {
    return this.selected ? this.selected.label : '';
  }

  static US_ALIASES = ['usa', 'united states', 'united states of america'];

  /** Whether an item is in, or is, the merchant's priority country. */
  isPriority(item) {
    if (!this.priorityCountry) return false;
    const country = DestinationCombobox.fold(
      item.isCountry ? item.label : item.sub,
    );
    if (!country) return false;
    if (country === this.priorityCountry) return true;
    const aliases = DestinationCombobox.US_ALIASES;
    return aliases.includes(country) && aliases.includes(this.priorityCountry);
  }

  /** Lower-cased and stripped of accents, so "sao" finds "São Paulo". */
  static fold(text) {
    return String(text || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .trim();
  }

  /** @returns {boolean} Whether the current selection is still among the items. */
  setItems(items) {
    this.items = (Array.isArray(items) ? items : []).map((item) => ({
      ...item,
      id: String(item.id),
      folded: DestinationCombobox.fold(item.search || item.label),
      foldedLabel: DestinationCombobox.fold(item.label),
      priority: this.isPriority(item),
    }));

    const kept = this.selected
      ? this.items.find((item) => item.id === this.selected.id) || null
      : null;
    if (this.selected && !kept) {
      this.selected = null;
      this.input.value = '';
    } else if (kept) {
      this.selected = kept;
      this.input.value = kept.label;
    }
    this.syncClearButton();
    if (this.isOpen) this.render();
    return Boolean(kept) || !this.selected;
  }

  bindEvents() {
    this.input.addEventListener('input', () => {
      // Typing is a search, not a selection: the current destination stays
      // applied until the shopper picks another or clears, and the text is
      // put back to its label if they wander off without picking (`close`).
      this.syncClearButton();
      this.open();
    });
    // The sticky buy bar stands aside for as long as the input has focus —
    // i.e. for as long as the phone keyboard is up — not only while the list
    // is open: picking a result closes the list, but the keyboard stays until
    // the input lets go, and the bar came back across the cards underneath it.
    this.input.addEventListener('focus', () => {
      document.documentElement.setAttribute('data-experiences-search-open', '');
      this.open();
    });
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));
    this.input.addEventListener('blur', () => {
      // Deferred so a click on an option lands before the list goes away.
      setTimeout(() => {
        if (this.root.contains(document.activeElement)) return;
        this.close(true);
        document.documentElement.removeAttribute(
          'data-experiences-search-open',
        );
      }, 120);
    });

    // Keep focus in the input while an option is clicked.
    this.list.addEventListener('mousedown', (event) => event.preventDefault());
    this.list.addEventListener('click', (event) => {
      const option = event.target.closest('[data-combobox-index]');
      if (!option) return;
      this.select(this.results[Number(option.dataset.comboboxIndex)]);
    });
    this.list.addEventListener('mousemove', (event) => {
      const option = event.target.closest('[data-combobox-index]');
      if (option) this.setActive(Number(option.dataset.comboboxIndex), false);
    });

    this.clearButton?.addEventListener('click', () => {
      this.clear(false);
      this.input.focus();
    });
  }

  onKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this.isOpen) this.open();
        this.setActive(
          Math.min(this.activeIndex + 1, this.results.length - 1),
          true,
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.setActive(Math.max(this.activeIndex - 1, 0), true);
        break;
      case 'Enter': {
        const pick =
          this.results[this.activeIndex] ||
          (this.results.length === 1 ? this.results[0] : null);
        if (pick) {
          event.preventDefault();
          this.select(pick);
        }
        break;
      }
      case 'Escape':
        if (this.isOpen) {
          event.preventDefault();
          this.close(true);
        }
        break;
      case 'Tab':
        this.close(true);
        break;
      default:
        break;
    }
  }

  search(query) {
    const folded = DestinationCombobox.fold(query);
    const byPriority = (a, b) =>
      Number(b.priority) - Number(a.priority) ||
      (b.count || 0) - (a.count || 0) ||
      a.label.localeCompare(b.label);
    if (!folded) {
      // Nothing typed: the priority country and every one of its cities
      // (busiest first) lead, then the remaining countries to browse. Not
      // capped — this is the one browseable view, and the list scrolls.
      const priority = this.items
        .filter((item) => item.priority)
        .sort(
          (a, b) =>
            Number(b.isCountry) - Number(a.isCountry) || byPriority(a, b),
        );
      const others = this.items
        .filter((item) => item.isCountry && !item.priority)
        .sort(byPriority);
      return priority.concat(others);
    }
    const ranked = [];
    this.items.forEach((item) => {
      let rank;
      if (item.foldedLabel === folded) rank = 0;
      else if (item.foldedLabel.startsWith(folded)) rank = 1;
      else if (item.folded.includes(` ${folded}`)) rank = 2;
      else if (item.folded.includes(folded)) rank = 3;
      else return;
      ranked.push({ item, rank });
    });
    // Within a rank the priority country comes first ("san" leads with San
    // Diego and San Francisco, not San Cristóbal), then the busier
    // destination — "par" should offer Paris before Paracas — and only then
    // the alphabet.
    ranked.sort((a, b) => a.rank - b.rank || byPriority(a.item, b.item));
    return ranked
      .slice(0, DestinationCombobox.MAX_RESULTS)
      .map((entry) => entry.item);
  }

  open() {
    const wasOpen = this.isOpen;
    this.isOpen = true;
    this.list.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.render();
    if (!wasOpen) {
      this.watchViewport();
      // The list always opens downward (the card it sits in clips anything
      // above); when the input is near the bottom of the screen, bring the
      // list into view rather than leaving it off-screen. Once per opening.
      this.list.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    this.fitToViewport();
  }

  /**
   * Sizes the list to what is actually visible. On a phone the keyboard takes
   * half the screen and `window.innerHeight` does not know it; the visual
   * viewport does. Re-run while open, because the keyboard arrives a beat
   * after focus and the page scrolls under it.
   */
  fitToViewport() {
    const viewport = window.visualViewport;
    const bottom = viewport
      ? viewport.offsetTop + viewport.height
      : window.innerHeight;
    const available = bottom - this.list.getBoundingClientRect().top - 12;
    const height = Math.max(
      DestinationCombobox.MIN_LIST_HEIGHT,
      Math.min(DestinationCombobox.MAX_LIST_HEIGHT, available),
    );
    this.list.style.maxHeight = `${Math.round(height)}px`;
  }

  watchViewport() {
    if (this.viewportHandler) return;
    this.viewportHandler = () => {
      if (this.isOpen) this.fitToViewport();
    };
    window.visualViewport?.addEventListener('resize', this.viewportHandler);
    window.visualViewport?.addEventListener('scroll', this.viewportHandler);
    window.addEventListener('resize', this.viewportHandler);
  }

  unwatchViewport() {
    if (!this.viewportHandler) return;
    window.visualViewport?.removeEventListener('resize', this.viewportHandler);
    window.visualViewport?.removeEventListener('scroll', this.viewportHandler);
    window.removeEventListener('resize', this.viewportHandler);
    this.viewportHandler = null;
  }

  /** @param {boolean} settle - Put the input text back to the selection. */
  close(settle) {
    this.isOpen = false;
    this.list.hidden = true;
    this.list.style.maxHeight = '';
    this.unwatchViewport();
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.activeIndex = -1;
    if (settle) {
      this.input.value = this.selectedLabel;
      this.syncClearButton();
    }
  }

  render() {
    const query = this.input.value;
    this.results = this.search(query);
    this.activeIndex = -1;
    this.list.replaceChildren();

    const hint = document.createElement('li');
    hint.className = 'combobox__hint';
    hint.setAttribute('role', 'presentation');
    if (!DestinationCombobox.fold(query)) {
      hint.textContent = exploreStringWith('filter_search_hint', {
        count: formatNumber(this.items.length),
      });
      this.list.append(hint);
    } else if (!this.results.length) {
      hint.textContent = exploreStringWith('filter_no_results', {
        query: query.trim(),
      });
      this.list.append(hint);
    }

    this.results.forEach((item, index) => {
      const option = document.createElement('li');
      option.className = 'combobox__option';
      option.id = `${this.uid}-option-${index}`;
      option.setAttribute('role', 'option');
      option.setAttribute(
        'aria-selected',
        this.selected?.id === item.id ? 'true' : 'false',
      );
      option.dataset.comboboxIndex = String(index);

      const label = document.createElement('span');
      label.className = 'combobox__option-label';
      label.textContent = item.label;
      option.append(label);

      if (item.sub) {
        const sub = document.createElement('span');
        sub.className = 'combobox__option-sub';
        sub.textContent = item.sub;
        option.append(sub);
      }
      this.list.append(option);
    });
  }

  setActive(index, scroll) {
    const options = this.list.querySelectorAll('[data-combobox-index]');
    if (!options.length) return;
    this.activeIndex = Math.max(0, Math.min(index, options.length - 1));
    options.forEach((option, i) => {
      option.classList.toggle('is-active', i === this.activeIndex);
    });
    const active = options[this.activeIndex];
    this.input.setAttribute('aria-activedescendant', active.id);
    if (scroll) active.scrollIntoView({ block: 'nearest' });
  }

  select(item) {
    if (!item) return;
    const changed = this.selected?.id !== item.id;
    this.selected = item;
    this.input.value = item.label;
    this.close(false);
    this.syncClearButton();
    // On a touch screen the pick is the end of the interaction: let the input
    // go so the keyboard drops and the filtered cards are the thing on screen.
    // With a physical keyboard focus stays, so Tab moves on from here.
    if (window.matchMedia('(pointer: coarse)').matches) this.input.blur();
    if (changed) this.emitChange();
  }

  /** @param {boolean} silent - Reset without announcing a change. */
  clear(silent) {
    const hadValue = Boolean(this.selected);
    this.selected = null;
    this.input.value = '';
    this.close(false);
    this.syncClearButton();
    if (hadValue && !silent) this.emitChange();
  }

  syncClearButton() {
    if (!this.clearButton) return;
    this.clearButton.hidden = !this.input.value;
  }

  emitChange() {
    this.root.dispatchEvent(
      new CustomEvent('combobox:change', { bubbles: true }),
    );
  }
}

class ExploreExperiencesMap {
  static SELECTORS = {
    root: '[data-explore-experiences-map]',
    canvas: '[data-explore-experiences-map-canvas]',
  };

  static DEFAULT_STYLE = 'mapbox://styles/everwow/cms65cg5l002d01sf8wjqdggy';
  static GLYPHS_URL = 'mapbox://fonts/everwow/{fontstack}/{range}.pbf';

  /**
   * Opening views, one per breakpoint.
   *
   * `desktop` is the North Atlantic, framing North America and Western Europe
   * together — where the buyers are. South-west corner reaches down to the
   * Caribbean (Dominican Republic / Jamaica / Puerto Rico are large bubbles),
   * north-east to southern Scandinavia.
   *
   * That span is ~147° wide, so on a portrait phone it fits to the narrow axis
   * and both continents collapse into a pair of mega-clusters with nothing
   * readable between them. `mobile` opens on the contiguous United States
   * instead — the largest single market, and wide enough that the fit still
   * lands on the horizontal axis.
   */
  static HOME_VIEW = {
    desktop: {
      bounds: [
        [-125, 18],
        [22, 60],
      ],
      padding: 32,
    },
    mobile: {
      bounds: [
        [-125, 24.5],
        [-66.9, 49.4],
      ],
      padding: 16,
    },
  };

  static getThemeColors() {
    const root = document.documentElement;
    const get = (name, fallback) =>
      getComputedStyle(root).getPropertyValue(name).trim() || fallback;

    return {
      blue3: get('--shop-color-blue-3', '#84ACDB'),
      blue4: get('--shop-color-blue-4', '#11324F'),
      blue5: get('--shop-color-blue-5', '#0A1E2F'),
      orange4: get('--shop-color-orange-4', '#F96248'),
      neutral1: get('--shop-color-neutral-1', '#FFFFFF'),
      neutral8: get('--shop-color-neutral-8', '#0A0C0D'),
    };
  }

  /**
   * @param {HTMLElement} root - The map's root container element.
   */
  constructor(root) {
    this.root = root;
    this.canvas = root.querySelector(ExploreExperiencesMap.SELECTORS.canvas);
    this.map = null;
    this.destinations = [];
    this.curated = [];
    this.curatedByDestination = new Map();
    this.destinationsById = new Map();
    this.styleReady = false;
    this.section = root.closest('.section-explore-experiences');
    // Token/style/label sit on the section element rather than this root, so
    // the details modal can also be built on pages that render no map.
    this.config = root.closest('.explore_experiences')?.dataset || {};

    const modalEl = this.section?.querySelector(
      '[data-experience-details-modal]',
    );
    if (modalEl && !modalEl._experienceModal) {
      modalEl._experienceModal = new ExperienceModal(modalEl, {
        mapboxToken: this.config.mapboxToken,
        mapboxStyle: this.config.mapboxStyle,
        reviewsLabel: this.config.reviewsLabel,
      });
    }
    this.experienceModal = modalEl?._experienceModal || null;
  }

  /** Whether this instance has everythinfg it needs to render a map. */
  canInit() {
    return (
      this.root.dataset.mapReady !== 'true' &&
      typeof mapboxgl !== 'undefined' &&
      Boolean(this.config.mapboxToken) &&
      Boolean(this.canvas)
    );
  }

  init(productPath) {
    if (!this.canInit()) return;
    this.productPath = productPath || null;
    this.root.dataset.mapReady = 'true';

    mapboxgl.accessToken = this.config.mapboxToken;
    this.map = this.createMap();
    this.map.addControl(
      new mapboxgl.NavigationControl({ showCompass: false }),
      'bottom-right',
    );

    this.panel = new DestinationPanel(this.section, {
      productPath: this.productPath,
      reviewsLabel: this.config.reviewsLabel,
    });

    this.map.once('style.load', () => {
      const style = this.map.getStyle();
      style.glyphs = ExploreExperiencesMap.GLYPHS_URL;
      this.map.setStyle(style);

      this.map.once('style.load', () => {
        this.addDestinationSource();
        this.addDestinationLayers();
        this.bindMapEvents();
        this.styleReady = true;
        this.applyData();

        requestAnimationFrame(() => this.map.resize());
      });
    });
  }

  /**
   * Curated experiences carry no destinationId, only the coordinates of the
   * destination they sit in, so they are joined onto destinations by rounded
   * coordinate with a city/country name fallback.
   * @param {Array<object>} curated
   */
  setCurated(curated) {
    this.curated = Array.isArray(curated) ? curated : [];
    this.indexCurated();
    this.applyData();
  }

  /**
   * @param {Array<object>} points - Raw /map-points response array.
   */
  setDestinations(points) {
    this.destinations = (Array.isArray(points) ? points : []).filter(
      ({ coordinates }) =>
        !Number.isNaN(parseFloat(coordinates?.longitude)) &&
        !Number.isNaN(parseFloat(coordinates?.latitude)),
    );
    this.destinationsById = new Map(
      this.destinations.map((point) => [point.destinationId, point]),
    );
    this.indexCurated();
    this.applyData();
  }

  static coordinateKey(latitude, longitude) {
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    return `${lat.toFixed(3)}:${lng.toFixed(3)}`;
  }

  static nameKey(name, country) {
    if (!name) return null;
    return `${name}|${country || ''}`.toLowerCase();
  }

  indexCurated() {
    this.curatedByDestination = new Map();
    if (!this.destinations.length || !this.curated.length) return;

    const byCoordinate = new Map();
    const byName = new Map();
    this.destinations.forEach((point) => {
      const coordinateKey = ExploreExperiencesMap.coordinateKey(
        point.coordinates.latitude,
        point.coordinates.longitude,
      );
      if (coordinateKey) byCoordinate.set(coordinateKey, point.destinationId);

      const nameKey = ExploreExperiencesMap.nameKey(point.name, point.country);
      if (nameKey) byName.set(nameKey, point.destinationId);
    });

    this.curated.forEach((item) => {
      const location = item.location || {};
      const coordinates = location.coordinates || {};
      const destinationId =
        byCoordinate.get(
          ExploreExperiencesMap.coordinateKey(
            coordinates.latitude,
            coordinates.longitude,
          ),
        ) ??
        byName.get(
          ExploreExperiencesMap.nameKey(location.city, location.country),
        );
      if (destinationId === undefined) return;

      const list = this.curatedByDestination.get(destinationId) || [];
      list.push(item);
      this.curatedByDestination.set(destinationId, list);
    });
  }

  /** Pushes the current destination + curated join into the map source. */
  applyData() {
    if (!this.styleReady || !this.map?.getSource('experiences-destinations')) {
      return;
    }

    const features = this.destinations.map((point) => ({
      type: 'Feature',
      properties: {
        destinationId: point.destinationId,
        name: point.name,
        country: point.country,
        count: Number(point.count) || 0,
        curatedCount: (this.curatedByDestination.get(point.destinationId) || [])
          .length,
      },
      geometry: {
        type: 'Point',
        coordinates: [
          parseFloat(point.coordinates.longitude),
          parseFloat(point.coordinates.latitude),
        ],
      },
    }));

    this.map
      .getSource('experiences-destinations')
      .setData({ type: 'FeatureCollection', features });
  }

  /**
   * The opening frame for the current breakpoint. `--map-desktop` is flipped by
   * the `md:` variant on the root element, so the cutover is owned by the
   * theme's breakpoint rather than a pixel width duplicated here. Anything
   * other than an explicit `0` keeps the desktop frame, so a missing property
   * degrades to the wider view rather than pinning everyone to the US.
   * @returns {{bounds: Array<Array<number>>, padding: number}}
   */
  homeView() {
    const flag = getComputedStyle(this.root)
      .getPropertyValue('--map-desktop')
      .trim();

    return flag === '0'
      ? ExploreExperiencesMap.HOME_VIEW.mobile
      : ExploreExperiencesMap.HOME_VIEW.desktop;
  }

  createMap() {
    const { mapboxStyle } = this.config;
    const home = this.homeView();

    this.canvas.innerHTML = '';

    return new mapboxgl.Map({
      container: this.canvas,
      style: mapboxStyle || ExploreExperiencesMap.DEFAULT_STYLE,
      // Framed at construction rather than eased into once /map-points lands,
      // so the opening view is never a stale center the shopper watches jump.
      bounds: home.bounds,
      fitBoundsOptions: { padding: home.padding },
      locale: cooperativeGesturesLocale(),
      // The map is a full-width block in the middle of a long page, so an
      // unqualified wheel/one-finger gesture has to keep scrolling the page:
      // zoom needs ctrl/⌘ + scroll, panning on touch needs two fingers, and
      // Mapbox explains that in an overlay the first time either is blocked.
      cooperativeGestures: true,
      // Nothing on this map reads in 3D, and a two-finger vertical drag —
      // the gesture cooperative mode makes shoppers use to pan — otherwise
      // tilts the whole thing into a pitched plane they cannot reset.
      touchPitch: false,
      pitchWithRotate: false,
      // Zooming re-clusters, so most bubbles get a new total. Mapbox fades
      // symbols over 300ms by default, which reads as the numbers lagging a
      // beat behind the bubbles they sit on; 0 swaps them on the same frame.
      fadeDuration: 0,
      // At world zoom repeated copies would show every destination two or
      // three times, and double-count them in the in-view total.
      renderWorldCopies: false,
    });
  }

  /**
   * A single clustered source holds every destination. Clusters sum their
   * children's experience counts (rather than counting pins) so a cluster
   * label reads as "how many experiences are under here".
   */
  addDestinationSource() {
    this.map.addSource('experiences-destinations', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
      cluster: true,
      clusterMaxZoom: 7,
      // Wide enough that the biggest bubbles do not pile onto each other at
      // world zoom, where the largest clusters are 40-50px across.
      clusterRadius: 80,
      clusterProperties: {
        count: ['+', ['get', 'count']],
        curatedCount: ['+', ['get', 'curatedCount']],
      },
    });
  }

  /**
   * One bubble layer serves both clustered and unclustered features: a cluster
   * carries a summed `count`, a lone destination carries its own, so the same
   * size/colour ramp applies to both. Size is the primary signal for "how many
   * experiences are here"; an orange ring flags a curated pick inside.
   */
  addDestinationLayers() {
    const colors = ExploreExperiencesMap.getThemeColors();
    const scale = ['sqrt', ['max', ['get', 'count'], 1]];
    const hasCurated = ['>', ['coalesce', ['get', 'curatedCount'], 0], 0];

    this.map.addLayer({
      id: 'destination-bubble',
      type: 'circle',
      source: 'experiences-destinations',
      paint: {
        'circle-color': [
          'step',
          ['get', 'count'],
          colors.blue3,
          25,
          colors.blue4,
          250,
          colors.blue5,
        ],
        'circle-radius': [
          'interpolate',
          ['linear'],
          scale,
          1,
          14,
          5,
          18,
          16,
          24,
          55,
          34,
          130,
          44,
          380,
          56,
        ],
        'circle-opacity': 0.94,
        'circle-stroke-width': ['case', hasCurated, 3, 1.5],
        'circle-stroke-color': [
          'case',
          hasCurated,
          colors.orange4,
          colors.neutral1,
        ],
      },
    });

    this.map.addLayer({
      id: 'destination-bubble-count',
      type: 'symbol',
      source: 'experiences-destinations',
      layout: {
        'text-field': ['number-format', ['get', 'count'], { locale: 'en-US' }],
        'text-font': ['Gelasio Bold', 'Arial Unicode MS Bold'],
        'text-size': [
          'interpolate',
          ['linear'],
          scale,
          1,
          11,
          5,
          12,
          16,
          14,
          55,
          16,
          130,
          18,
          380,
          20,
        ],
        'text-allow-overlap': true,
      },
      paint: {
        // Blue/3 bubbles are too light to carry white type.
        'text-color': [
          'step',
          ['get', 'count'],
          colors.blue5,
          25,
          colors.neutral1,
        ],
      },
    });
  }

  bindMapEvents() {
    this.map.on('click', 'destination-bubble', (event) => {
      const feature = event.features[0];
      const { cluster_id: clusterId } = feature.properties;

      if (clusterId !== undefined) {
        this.map
          .getSource('experiences-destinations')
          .getClusterExpansionZoom(clusterId, (error, zoom) => {
            if (!error) {
              this.map.easeTo({ center: feature.geometry.coordinates, zoom });
            }
          });
        return;
      }

      this.openDestination(feature);
    });

    this.map.on('mouseenter', 'destination-bubble', () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });
    this.map.on('mouseleave', 'destination-bubble', () => {
      this.map.getCanvas().style.cursor = '';
    });
  }

  openDestination(feature) {
    const { destinationId } = feature.properties;
    const destination = this.destinationsById.get(destinationId);
    if (!destination || !this.panel) return;

    this.map.easeTo({
      center: feature.geometry.coordinates,
      zoom: Math.max(this.map.getZoom(), 8),
      duration: 500,
    });

    this.panel.open(destination, this.curatedByDestination.get(destinationId));
  }

  /** Returns to this breakpoint's opening frame (used after a variant swap). */
  flyHome() {
    const home = this.homeView();

    this.map?.fitBounds(home.bounds, {
      padding: home.padding,
      duration: 700,
    });
  }

  openExperienceDetails(experience) {
    if (!this.experienceModal || !experience?.id || !this.productPath) return;

    this.experienceModal.open(experience, (item) =>
      this.fetchJson(
        `${this.productPath}/experience/${encodeURIComponent(item.id)}`,
      ).then((data) => ExperienceModal.mapDetail(data, item)),
    );
  }
  async fetchJson(url) {
    return fetchJson(url);
  }
}

/**
 * Slide-over panel listing every experience available at one destination.
 * Opened by clicking a destination bubble; curated picks (from the map's own
 * /curated set) are shown first and badged, the rest are paged in from
 * /eligible?destinationId=.
 */
class DestinationPanel {
  // Enough placeholder rows to fill the panel to its max height on both
  // breakpoints; fewer are shown when the destination holds fewer experiences.
  static SKELETON_ROWS = 6;

  constructor(section, { productPath = null, reviewsLabel = null } = {}) {
    this.section = section;
    this.productPath = productPath;
    this.reviewsLabel = reviewsLabel;
    this.root = section?.querySelector('[data-destination-panel]');
    this.list = this.root?.querySelector('[data-destination-panel-list]');
    this.loadMoreButton = this.root?.querySelector(
      '[data-destination-panel-load-more]',
    );
    this.loadMoreFooter = this.root?.querySelector(
      '[data-destination-panel-footer]',
    );
    this.template = document.querySelector('[data-destination-card-template]');
    this.skeletonTemplate = document.querySelector(
      '[data-destination-card-skeleton-template]',
    );
    this.destination = null;
    this.curated = [];
    this.response = null;
    this.seen = new Set();
    this.requestId = 0;
    this.isLoading = false;
    this.scrollSettleTimer = null;

    this.bindEvents();
  }

  bindEvents() {
    if (!this.root) return;

    this.root
      .querySelector('[data-destination-panel-close]')
      ?.addEventListener('click', () => this.close());

    this.loadMoreButton?.addEventListener('click', () => this.loadNextPage());

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen()) this.close();
    });
  }

  isOpen() {
    return Boolean(this.root && !this.root.classList.contains('invisible'));
  }

  /**
   * Supersedes any request still in flight. Its response is discarded by the
   * requestId guard in `loadNextPage`, which also skips that request's cleanup
   * — so the lock has to be released here, or a second bubble clicked while the
   * first was still loading would leave the panel stuck loading forever.
   */
  cancelPendingLoad() {
    this.requestId += 1;
    this.isLoading = false;
  }

  open(destination, curated = []) {
    if (!this.root || !destination) return;

    this.destination = destination;
    this.curated = curated || [];
    this.response = null;
    this.seen = new Set();
    this.cancelPendingLoad();

    this.root.querySelector('[data-destination-panel-title]').textContent =
      destination.name || '';
    const included = exploreString('experiences_included');
    this.root.querySelector(
      '[data-destination-panel-subtitle]',
    ).textContent = `${formatNumber(destination.count)} ${included}${
      destination.country ? ` · ${destination.country}` : ''
    }`;

    this.list.replaceChildren();
    this.renderItems(this.curated, true);
    this.setLoading(true);

    this.root.classList.remove('invisible', 'opacity-0');
    this.root.classList.add('opacity-100');

    this.scrollIntoView();
    this.loadNextPage();
  }

  /**
   * Brings the foot of the panel — which is the foot of the map box, at both
   * breakpoints — up to the bottom of what the shopper can see. A shopper can
   * reach the bubbles long before scrolling to the end of the section, and
   * without this the panel opens with its list and "Load more" below the fold.
   *
   * Measured against the *visual* viewport, not `innerHeight`: a phone's
   * browser toolbar overlays the layout viewport, so aligning to `innerHeight`
   * parks the footer behind the toolbar and the CTA still reads as cut off.
   *
   * Only ever scrolls down: if the panel already ends above the fold it is
   * fully visible, and pulling the page back up would be pure noise.
   */
  scrollIntoView() {
    const viewport = window.visualViewport;
    const visibleBottom = viewport
      ? viewport.offsetTop + viewport.height
      : window.innerHeight;
    const overhang = Math.round(
      this.root.getBoundingClientRect().bottom - visibleBottom,
    );
    if (overhang <= 1) return;

    const startY = window.scrollY;
    const target = startY + overhang;
    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    window.scrollTo({
      top: target,
      behavior: reduceMotion ? 'instant' : 'smooth',
    });
    this.settleScroll(target);
  }

  /**
   * A smooth scroll asked for from a tap handler is routinely dropped on touch
   * devices: the finger that opened the panel still counts as a scroll gesture,
   * and the browser cancels the programmatic animation — leaving the page
   * exactly where it was. So check back once the animation should have run and,
   * if the page has stopped short of the mark, take the jump instead.
   *
   * The two samples are what separates "cancelled" from "still easing"; a
   * shopper who has scrolled past the target on their own is left alone.
   * @param {number} target - Scroll position the panel is aligned at.
   */
  settleScroll(target) {
    clearTimeout(this.scrollSettleTimer);
    this.scrollSettleTimer = setTimeout(() => {
      const settled = window.scrollY;

      this.scrollSettleTimer = setTimeout(() => {
        if (window.scrollY !== settled) return;
        if (window.scrollY >= target - 2) return;

        window.scrollTo({ top: target, behavior: 'instant' });
      }, 120);
    }, 400);
  }

  close() {
    if (!this.root) return;
    // A panel closed before the alignment settled must not drag the page after
    // it — the shopper is looking at the map again by then.
    clearTimeout(this.scrollSettleTimer);
    this.cancelPendingLoad();
    this.root.classList.add('invisible', 'opacity-0');
    this.root.classList.remove('opacity-100');
    this.destination = null;
  }

  async loadNextPage() {
    if (this.isLoading || !this.destination || !this.productPath) return;
    if (this.response && !this.response.hasNext) return;

    const requestId = this.requestId;
    this.isLoading = true;
    this.setLoading(true);
    this.showSkeletons();

    try {
      const params = new URLSearchParams({
        destinationId: String(this.destination.destinationId),
      });
      if (this.response?.nextCursor) {
        params.set('cursor', this.response.nextCursor);
      }

      const data = await this.fetchPage(params);
      if (requestId !== this.requestId) return;

      this.response = data;
      this.renderItems(Array.isArray(data?.content) ? data.content : [], false);
    } catch (error) {
      console.error('Failed to load destination experiences:', error);
      this.response = { hasNext: false };
    } finally {
      // A superseded request must leave the skeletons alone: open() has
      // already rebuilt the list (and its own skeletons) for the new
      // destination by the time this cleanup runs.
      if (requestId === this.requestId) {
        this.removeSkeletons();
        this.isLoading = false;
        this.setLoading(false);
      }
    }
  }

  fetchPage(params) {
    return fetchJson(`${this.productPath}/eligible?${params.toString()}`);
  }

  setLoading(isLoading) {
    if (!this.loadMoreButton) return;
    const hasMore = this.response ? this.response.hasNext : true;
    // Hide the whole footer, not just the button — otherwise its border and
    // padding stay behind as an empty bar once the last page has loaded.
    this.loadMoreFooter?.classList.toggle('hidden', !hasMore && !isLoading);
    this.loadMoreButton.classList.toggle('opacity-50', isLoading);
    this.loadMoreButton.disabled = isLoading;
  }

  /**
   * Placeholder rows appended while a page of /eligible is in flight, so the
   * panel opens at (roughly) its final height instead of growing when the
   * response lands. Capped to what is left of the destination's own count so
   * a two-experience destination doesn't get six ghost rows.
   */
  showSkeletons() {
    if (!this.list || !this.skeletonTemplate) return;

    const total = Number(this.destination?.count) || 0;
    const remaining = total
      ? total - this.seen.size
      : DestinationPanel.SKELETON_ROWS;
    const count = Math.min(
      Math.max(remaining, 1),
      DestinationPanel.SKELETON_ROWS,
    );

    for (let i = 0; i < count; i += 1) {
      this.list.append(
        this.skeletonTemplate.content.cloneNode(true).firstElementChild,
      );
    }
  }

  removeSkeletons() {
    this.list
      ?.querySelectorAll('[data-destination-card-skeleton]')
      .forEach((el) => el.remove());
  }

  renderItems(items, isCurated) {
    if (!this.list || !this.template) return;

    items.forEach((item) => {
      const code = item.productCode;
      if (code && this.seen.has(code)) return;
      if (code) this.seen.add(code);

      this.list.append(this.buildCard(item, isCurated));
    });
  }

  buildCard(item, isCurated) {
    const card = this.template.content.cloneNode(true).firstElementChild;
    const rating = Number(item.combinedAverageRating) || 0;
    const reviews = Number(item.totalReviews) || 0;

    const image = card.querySelector('[data-card-image]');
    if (item.coverImageUrl) {
      image.src = item.coverImageUrl;
      image.alt = item.title || '';
    } else {
      image.remove();
    }

    card.querySelector('[data-card-title]').textContent = item.title || '';
    card.querySelector('[data-card-stars]').innerHTML =
      ExperiencesList.buildStars(rating);
    card.querySelector('[data-card-rating]').textContent = rating.toFixed(1);
    card.querySelector('[data-card-reviews]').textContent =
      ExperiencesList.formatReviews(reviews);

    const badge = card.querySelector('[data-card-badge]');
    if (isCurated) {
      badge.hidden = false;
    } else {
      badge.remove();
    }

    // The curated block comes from /curated, which carries no flag of its
    // own; the modal badges from this.
    card.addEventListener('click', () =>
      this.openExperience({ ...item, curated: isCurated }),
    );

    return card;
  }

  openExperience(item) {
    const modal = this.section?.querySelector(
      '[data-experience-details-modal]',
    )?._experienceModal;
    if (!modal || !item?.productCode || !this.productPath) return;

    // `prepare` is the same normaliser the cards use, so the modal is handed
    // the shape it populates from without a second mapping here.
    modal.open(ExperiencesList.prepare(item)._experience, (summary) =>
      fetchJson(
        `${this.productPath}/experience/${encodeURIComponent(summary.id)}`,
      ).then((data) => ExperienceModal.mapDetail(data, summary)),
    );
  }
}

/**
 * The details dialog: one experience at a time.
 *
 * A tap on any card in the section opens that experience here, and reaching
 * another one means closing this and tapping the next. It used to be a
 * carousel of the whole list behind prev/next arrows and swipes; that was
 * dropped so the dialog reads as a detail view rather than a second list, and
 * so the list behind it is the only thing that pages.
 */
class ExperienceModal {
  /**
   * How many photos of one experience the gallery builds. The API returns every
   * photo the supplier uploaded — 35 is typical, and each one costs two <img>
   * elements, a main slide and a thumbnail. Nobody swipes 35 pictures of a
   * single tour, and the tail is what turned the modal into a memory problem on
   * iOS. Ten is generous for the job the gallery actually does.
   */
  static GALLERY_LIMIT = 10;

  constructor(modal, { mapboxToken, mapboxStyle, reviewsLabel }) {
    this.modal = modal;
    this.mapboxToken = mapboxToken;
    this.mapboxStyle = mapboxStyle;
    this.reviewsLabel = reviewsLabel;

    /** Where the card is cloned into; centres it in the dialog. */
    this.host = modal?.querySelector('[data-experience-modal-host]');

    /** @type {object|null} the open card's record; null while closed */
    this.panel = null;
    /**
     * Which open() a detail fetch belongs to. The dialog can be closed and
     * reopened on another card while a fetch is in flight; comparing this on
     * arrival is what stops the late answer landing in the wrong card.
     */
    this.openToken = null;

    this.map = null;
    this.mapMarker = null;
    this.mapHost = null;
    this.pageScrollY = 0;
    this.tornDown = true;

    this.bindEvents();
  }

  bindEvents() {
    if (!this.modal) return;

    // Delegated rather than wired per element: the card is cloned fresh on
    // every open, and every control — close, gallery arrows, gift CTA — lives
    // inside it.
    this.modal.addEventListener('click', (event) => this.onClick(event));
    this.modal.addEventListener('keydown', (event) => this.onKeydown(event));
    // A safety net, not the mechanism: `close` is queued as a task and does not
    // fire in every embedding, so `close()` below tears down directly and this
    // only catches a close nothing else owns. Guarded on `open` because the
    // queue cuts both ways — an event from a previous close can be delivered
    // after the dialog has been reopened, and tore down a live one.
    this.modal.addEventListener('close', () => {
      if (!this.modal.open) this.teardown();
    });

    // If the shopper navigates away with the dialog open (the gift CTA leaves
    // it up until the cart paints), close it in pagehide: the handler runs
    // before the page is frozen into bfcache and nothing repaints on the way
    // out, so navigating back restores a clean PDP instead of flashing the
    // modal for a frame.
    window.addEventListener('pagehide', () => this.close());
  }

  onClick(event) {
    if (event.target.closest('[data-experience-modal-close]')) {
      this.close();
      return;
    }

    const descriptionToggle = event.target.closest(
      '[data-experience-modal-description-toggle]',
    );
    if (descriptionToggle) {
      this.toggleDescription(descriptionToggle);
      return;
    }

    const gift = event.target.closest('[data-experience-modal-gift-button]');
    if (gift) {
      this.giftCollection(gift);
      return;
    }

    const thumbStep = event.target.closest(
      '[data-experience-modal-thumbs-prev], [data-experience-modal-thumbs-next]',
    );
    if (thumbStep) {
      const thumbs = this.panel?.thumbsSwiper;
      if (thumbStep.hasAttribute('data-experience-modal-thumbs-next'))
        thumbs?.slideNext();
      else thumbs?.slidePrev();
      return;
    }

    // Anything inside the card is the card's own; a click on the backdrop
    // around it closes the dialog.
    if (event.target.closest('.experience-card')) return;
    this.close();
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.close();
  }

  /**
   * "Gift this collection" goes straight to the cart: add the PDP's selected
   * variant through the shared ProductCartHandler (which owns the `_pair`
   * property and the cart URL) and navigate. Deliberately NOT routed through
   * the PDP submit button — that path runs the upsell-modal flow, which closes
   * this dialog, flashes the PDP underneath and opens a second modal before
   * the cart. The dialog stays open while the request runs so the shopper goes
   * from modal to cart with nothing flashing in between (pagehide closes it
   * for the bfcache snapshot).
   * @param {HTMLButtonElement} button - The CTA, disabled while in flight.
   */
  async giftCollection(button) {
    const cart = window.EverwowProductCart;
    if (!cart) {
      // No cart handler on this page — fall back to the PDP's own submit.
      this.modal.close();
      ExperienceModal.addSelectedVariantToCart();
      return;
    }

    // Mirror the old path's sold-out behavior: a disabled submit means the
    // selected variant cannot be added, so the CTA stays a no-op.
    const submit = document.querySelector(
      '#product-form .product__add-to-cart',
    );
    if (submit?.disabled || this._gifting) return;

    // Only the button that was actually pressed is marked. The panel holds two
    // CTAs that do the same thing (the primary one and the pay-later twin), and
    // marking both would make pressing one light up the other as though they
    // were a single control. Each styles `aria-busy` its own way in
    // `snippets/experience-details-modal.liquid`.
    this._gifting = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const variantId = cart.getSelectedVariantId();
      if (!variantId) throw new Error('No variant selected');

      await cart.addGift(variantId);
      cart.redirectToCart();
    } catch (error) {
      console.error('ExperienceModal: gift this collection failed', error);
      this._gifting = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  /**
   * The CTA is left disabled while the gift request navigates away, and that
   * state would otherwise survive into the bfcache snapshot; the pagehide
   * close runs this so a restored or reopened modal gets a live button back.
   */
  resetGiftButton() {
    this._gifting = false;
    this.modal
      ?.querySelectorAll('[data-experience-modal-gift-button]')
      .forEach((button) => {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      });
  }

  /** Legacy fallback: click the PDP's real add-to-cart submit. */
  static addSelectedVariantToCart() {
    document.querySelector('#product-form .product__add-to-cart')?.click();
  }

  /**
   * Opens the dialog on one experience.
   *
   * The summary the opener already holds paints straight away, so the card is
   * never blank while its detail is in flight; `load` then fills in the
   * description, inclusions, itinerary, the rest of the gallery and the map.
   *
   * @param {object} experience - A summary in the `_experience` shape that
   *   `ExperiencesList.prepare` produces.
   * @param {(item: object) => Promise<object>} [load] - Fetches its full
   *   detail. Omitted, the summary is all the card ever shows.
   */
  open(experience, load = null) {
    if (!this.modal || !this.host || !experience) return;
    const template = document.querySelector('[data-experience-panel-template]');
    if (!template) return;

    // A card may already be up (the map's destination panel can open one over
    // another): swap it out rather than stacking.
    this.releasePanel();
    this.tornDown = false;
    const token = Symbol('experience');
    this.openToken = token;

    const el = template.content.cloneNode(true).firstElementChild;
    const panel = {
      item: experience,
      detail: null,
      el,
      scroll: el.querySelector('[data-experience-modal-scroll]'),
      mainSwiper: null,
      thumbsSwiper: null,
      galleryMq: null,
      onGalleryMqChange: null,
      gallerySignature: null,
    };
    this.panel = panel;
    this.host.replaceChildren(el);

    if (!this.modal.open) {
      const scrollY = window.scrollY;
      this.modal.showModal();
      // The dialog itself, not the close button showModal() would pick: a phone rings it even
      // on a tap. Tab still reaches the button; Escape still closes.
      this.modal.focus({ preventScroll: true });
      window.scrollTo({ top: scrollY, behavior: 'instant' });
      this.lockScroll();
    }

    this.populate(panel, experience);
    // The price line is empty in the template — the section fills every copy
    // of it on the page from the selected variant.
    SectionProductExploreExperiences.updateExperiencesPrices();
    this.mountMap(panel);

    const loader = typeof load === 'function' ? load : async (item) => item;
    Promise.resolve(loader(experience))
      .then((detail) => {
        // Closed, or reopened on another card, while the fetch was in flight.
        if (this.openToken !== token || this.panel !== panel) return;
        panel.detail = { ...experience, ...detail };
        this.populate(panel, panel.detail);
        this.mountMap(panel);
      })
      .catch((error) =>
        console.error('Failed to load experience details:', error),
      );
  }

  /** Takes the open card down; the map instance itself survives for the next. */
  releasePanel() {
    const panel = this.panel;
    if (!panel) return;

    this.unmountMap(panel);
    this.destroySlider(panel);
    // Set up per card by `setDescription`, and left observing a card that is
    // about to leave the document.
    panel.el
      .querySelector('[data-experience-modal-description-wrap]')
      ?._overflowObserver?.disconnect();
    // Watching a section inside a card that is about to leave the document.
    panel.reviewsObserver?.disconnect();
    panel.reviewsObserver = null;
    panel.el.remove();
    this.panel = null;
  }

  /**
   * The one way out. Everything that ends the dialog comes through here rather
   * than trusting the `close` event, because leaving the page scroll locked
   * with a live card in the DOM is the worst way to find out that event never
   * arrived.
   */
  close() {
    if (this.modal.open) this.modal.close();
    this.teardown();
  }

  teardown() {
    if (this.tornDown) return;
    this.tornDown = true;

    this.resetGiftButton();
    this.releasePanel();
    this.destroyMap();
    this.unlockScroll();
    // A detail still in flight checks this on arrival and drops itself.
    this.openToken = null;
  }

  static mapDetail(data, fallback = {}) {
    const coverImage =
      data.images?.find((img) => img.isCover)?.url || data.images?.[0]?.url;
    const coordinates = data.location?.coordinates || {};
    const location = data.location || {};
    return {
      title: data.title,
      rating: data.combinedAverageRating,
      reviews_amount: data.totalReviews,
      image: coverImage,
      // {url, thumbUrl} rather than a bare URL: the thumbnail rail renders at
      // 56px and the API now offers a 360x240 rendition for it. `thumbnail` is
      // absent on an older API, and on the summary shape the openers pass, so
      // every reader falls back to the full image.
      images: (data.images || [])
        .filter((img) => img?.url)
        .map((img) => ({
          url: img.url,
          thumbUrl: img.thumbnail?.url || img.url,
        })),
      description: data.description,
      inclusions: Array.isArray(data.inclusions) ? data.inclusions : [],
      exclusions: Array.isArray(data.exclusions) ? data.exclusions : [],
      itinerary: data.itinerary || null,
      languageGuides: Array.isArray(data.languageGuides)
        ? data.languageGuides
        : [],
      // Both are always on the wire — `[]`, never null — so branch on length,
      // not on nullness. The guard is for the OTHER shape: the summary the
      // openers hand `populate` before the detail lands has neither field, and
      // the reviews band renders against that first pass too.
      ratingDistribution: Array.isArray(data.ratingDistribution)
        ? data.ratingDistribution
        : [],
      reviewSources: Array.isArray(data.reviewSources)
        ? data.reviewSources
        : [],
      latitude: coordinates.latitude ?? fallback.latitude,
      longitude: coordinates.longitude ?? fallback.longitude,
      // The detail endpoint knows nothing about the gift; whether this row was
      // a curated pick, and how many people the gift covers for it, are the
      // opener's to say.
      curated: Boolean(fallback.curated),
      max_party_size: fallback.max_party_size ?? null,
      location_name:
        location.name ||
        location.address ||
        [location.city, location.country].filter(Boolean).join(', '),
    };
  }

  populate(panel, experience) {
    const root = panel.el;
    // Two shapes reach this: `mapDetail`'s {url, thumbUrl} pairs, and the bare
    // cover URL the list openers carry before the detail lands. Normalise, then
    // cap — the summary case is one image, so the slice only bites on details.
    const source = experience.images?.length
      ? experience.images
      : [experience.image].filter(Boolean);
    const images = source
      .map((image) =>
        typeof image === 'string' ? { url: image, thumbUrl: image } : image,
      )
      .filter((image) => image?.url)
      .slice(0, ExperienceModal.GALLERY_LIMIT);
    const gallery = root.querySelector('[data-experience-modal-gallery]');
    const mainSlides = root.querySelector(
      '[data-experience-modal-main-slides]',
    );
    const thumbnails = root.querySelector('[data-experience-modal-thumbnails]');

    root.querySelector('[data-experience-modal-title]').textContent =
      experience.title || '';

    this.setPartySize(root, experience.max_party_size);

    this.setDescription(root, experience.description);

    this.populateInclusions(root, experience);
    this.populateItinerary(root, experience);
    this.populateLanguages(root, experience);

    const ratingEl = root.querySelector('[data-experience-modal-rating]');
    const rating = parseFloat(experience.rating) || 0;
    const reviews = Number(experience.reviews_amount) || 0;
    ratingEl.querySelector('[data-experience-modal-rating-value]').textContent =
      Number.isInteger(rating) ? String(rating) : rating.toFixed(1);
    ratingEl.querySelector(
      '[data-experience-modal-stars-fill]',
    ).style.width = `${(rating / 5) * 100}%`;
    ratingEl.querySelector(
      '[data-experience-modal-rating-reviews]',
    ).textContent = `${formatNumber(reviews)} ${
      this.reviewsLabel || exploreString('reviews')
    }`;

    // The Viator attribution under the stars only makes sense when there is a
    // score or a review count to attribute.
    root
      .querySelector('[data-experience-modal-rating-source]')
      ?.classList.toggle('hidden', rating <= 0 && reviews <= 0);

    // Both of these have to run before the gallery's signature check below,
    // which returns early on the second pass whenever the photo set is
    // unchanged — and it usually is.
    this.populateRatingSource(root, experience);
    this.populateReviews(panel, experience);

    // Populate runs twice per card — once on the summary the opener already
    // had, once when the detail lands — and the two usually carry the same
    // cover image. Rebuilding an unchanged gallery would tear down a working
    // Swiper and flash the photo, so it only rebuilds when the set changes.
    // The detail fetch normally lands in a different task, so whichever pass
    // brings the real set builds the gallery once against real layout.
    const signature = images.map((image) => image.url).join('|');
    if (signature === panel.gallerySignature && panel.mainSwiper) return;
    panel.gallerySignature = signature;

    this.destroySlider(panel);
    mainSlides.replaceChildren();
    thumbnails.replaceChildren();

    if (!images.length) {
      gallery.classList.add('hidden');
      return;
    }

    gallery.classList.remove('hidden');

    // Every photo wears the card's two badges. Both come from the row that
    // opened the panel — the detail merges over it without either key.
    const cover = ExperiencesList.coverFor(
      experience.value,
      Number(experience.max_party_size) || 0,
    );

    images.forEach(({ url, thumbUrl }, position) => {
      const mainSlide = document
        .querySelector('[data-experience-main-slide-template]')
        .content.cloneNode(true).firstElementChild;
      ExperiencesList.fillCover(
        mainSlide.querySelector('.js-card-cover'),
        cover,
      );
      const mainImage = mainSlide.querySelector('img');
      // The template ships every slide lazy; the one that is already on screen
      // when the card opens must not wait for an intersection to be noticed.
      if (position === 0) {
        mainImage.loading = 'eager';
        mainImage.fetchPriority = 'high';
      }
      mainImage.src = url;
      mainImage.alt = experience.title || '';
      mainSlides.append(mainSlide);

      const thumbSlide = document
        .querySelector('[data-experience-thumb-slide-template]')
        .content.cloneNode(true).firstElementChild;
      const thumbImage = thumbSlide.querySelector('img');
      thumbImage.src = thumbUrl || url;
      thumbnails.append(thumbSlide);
    });

    this.initSlider(panel);
  }

  /**
   * The "Up to 4 people" line under the rating — the same fact the card
   * shows, from the list row that opened the modal. Hidden when the row had
   * none; the detail fetch cannot add it (see `mapDetail`).
   */
  setPartySize(root, size) {
    const line = root.querySelector('[data-experience-modal-party]');
    if (!line) return;
    const count = Number(size) || 0;
    line.hidden = !count;
    const text = line.querySelector('[data-experience-modal-party-text]');
    if (text) {
      text.textContent = count ? ExperiencesList.formatPartySize(count) : '';
    }
  }

  initSlider(panel) {
    if (typeof Swiper === 'undefined') return;

    const root = panel.el;
    const main = root.querySelector('[data-experience-modal-main-swiper]');
    const thumbs = root.querySelector('[data-experience-modal-thumbs-swiper]');
    if (!main || !thumbs || !main.querySelector('.swiper-slide')) return;

    const isDesktopGallery = () =>
      window.matchMedia('(min-width: 1280px)').matches;

    panel.thumbsSwiper = new Swiper(thumbs, {
      spaceBetween: 8,
      slidesPerView: 'auto',
      watchSlidesProgress: true,
      slideToClickedSlide: true,
      watchOverflow: true,
      freeMode: false,
      resistanceRatio: 0,
      breakpoints: { 1280: { direction: 'vertical' } },
      mousewheel: {
        enabled: isDesktopGallery(),
        releaseOnEdges: false,
        forceToAxis: true,
        sensitivity: 1,
      },
    });
    panel.mainSwiper = new Swiper(main, {
      navigation: {
        nextEl: root.querySelector('[data-experience-modal-next]'),
        prevEl: root.querySelector('[data-experience-modal-previous]'),
      },
      slidesPerView: 1,
      // Going on past the last photo lands back on the first, for a swipe, the arrow
      // and a tap alike, as on the PDP gallery in `product.js`. Rewind is symmetric in
      // Swiper, so the backward wrap (first -> last) is blocked below.
      rewind: true,
      // A slow swipe (over 300ms) turns the photo once it has moved a quarter of the
      // way. Swiper's default is half, and an unhurried thumb on a phone mostly fell
      // short of that and bounced back.
      longSwipesRatio: 0.25,
      // No edge resistance: on the last photo the drag follows the finger, so a slow
      // swipe there reaches the same quarter and goes on to the first, rather than
      // being damped to about half its length and bouncing back. The first photo never
      // drags backwards (see the lock below).
      resistance: false,
      thumbs: { swiper: panel.thumbsSwiper },
    });
    // Click/tap anywhere on the photo advances to the next slide (`rewind` takes it
    // back to the first at the end). Swiper suppresses this event after a drag
    // (`preventClicks`), so a swipe never double-counts as a click.
    panel.mainSwiper.on('click', (swiper, event) => {
      // The nav arrows live inside the main swiper, so their own click bubbles here
      // too — without this the "next" arrow would skip two slides.
      if (
        event.target.closest(
          '[data-experience-modal-next], [data-experience-modal-previous]',
        )
      )
        return;

      swiper.slideNext();
    });

    // The gallery only wraps forward: on the first photo block the backward direction
    // (a drag then holds still) and grey the arrow out, which Swiper never does for
    // either arrow while `rewind` is on.
    const previous = root.querySelector('[data-experience-modal-previous]');
    const lockBackwardWrap = (swiper) => {
      const atStart = swiper.isBeginning;
      swiper.allowSlidePrev = !atStart;
      if (previous) {
        previous.classList.toggle('swiper-button-disabled', atStart);
        previous.setAttribute('aria-disabled', String(atStart));
      }
    };
    panel.mainSwiper.on('toEdge fromEdge slideChange update', lockBackwardWrap);
    lockBackwardWrap(panel.mainSwiper);

    panel.mainSwiper.update();
    panel.thumbsSwiper.update();

    panel.galleryMq = window.matchMedia('(min-width: 1280px)');
    panel.onGalleryMqChange = (event) => {
      if (!panel.thumbsSwiper?.mousewheel) return;

      if (event.matches) panel.thumbsSwiper.mousewheel.enable();
      else panel.thumbsSwiper.mousewheel.disable();
    };
    panel.galleryMq.addEventListener('change', panel.onGalleryMqChange);
  }

  destroySlider(panel) {
    if (panel.galleryMq && panel.onGalleryMqChange) {
      panel.galleryMq.removeEventListener('change', panel.onGalleryMqChange);
    }
    panel.galleryMq = null;
    panel.onGalleryMqChange = null;

    panel.mainSwiper?.destroy(true, true);
    panel.thumbsSwiper?.destroy(true, true);
    panel.mainSwiper = null;
    panel.thumbsSwiper = null;
  }

  createMapMarker() {
    const colors = ExploreExperiencesMap.getThemeColors();
    const el = document.createElement('div');
    el.className =
      'flex h-12 w-12 items-center justify-center rounded-full bg-orange-4 shadow-lg';
    el.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 12C10.9 12 10 11.1 10 10C10 8.9 10.9 8 12 8C13.1 8 14 8.9 14 10C14 11.1 13.1 12 12 12ZM12 2C7.8 2 4 5.22 4 10.2C4 13.52 6.67 17.45 12 22C17.33 17.45 20 13.52 20 10.2C20 5.22 16.2 2 12 2Z" fill="${colors.neutral1}"/>
    </svg>`;
    return el;
  }

  /**
   * One Mapbox instance for the whole dialog, re-parented into whichever card
   * is open. A map per open would pay for a WebGL context every time; moving
   * the container keeps the canvas alive and costs a resize.
   */
  async mountMap(panel) {
    const container = panel.el.querySelector('[data-experience-modal-map]');
    if (!container) return;

    const experience = panel.detail || panel.item || {};
    const latitude = parseFloat(experience.latitude);
    const longitude = parseFloat(experience.longitude);
    if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
      container.classList.add('hidden');
      return;
    }

    // No token, no map: the block goes rather than standing empty (the collection pages' token is
    // a theme setting that may not be filled in).
    if (!this.mapboxToken) {
      container.classList.add('hidden');
      return;
    }
    container.classList.remove('hidden');

    try {
      await loadMapboxGl();
    } catch (error) {
      console.error('Failed to load Mapbox GL for experience modal:', error);
      return;
    }

    // The await leaves room for the shopper to close, or open another card
    // from the map panel; only the card still up gets the map.
    if (this.panel !== panel) return;

    const center = [longitude, latitude];

    if (this.map) {
      if (this.mapHost.parentElement !== container)
        container.appendChild(this.mapHost);
      this.map.resize();
      this.map.jumpTo({ center, zoom: 13 });
      this.mapMarker?.setLngLat(center);
      return;
    }

    this.mapHost = document.createElement('div');
    this.mapHost.className = 'h-full w-full';
    container.appendChild(this.mapHost);

    mapboxgl.accessToken = this.mapboxToken;
    this.map = new mapboxgl.Map({
      container: this.mapHost,
      style: this.mapboxStyle || ExploreExperiencesMap.DEFAULT_STYLE,
      center,
      zoom: 13,
      attributionControl: true,
      // Same reasoning as the section map: this one sits inside the card's own
      // scroll container, so a plain wheel or one-finger drag has to reach
      // that container rather than being swallowed by the map.
      locale: cooperativeGesturesLocale(),
      cooperativeGestures: true,
      touchPitch: false,
      pitchWithRotate: false,
    });

    this.map.addControl(
      new mapboxgl.NavigationControl({ showCompass: false }),
      'bottom-right',
    );

    this.map.on('load', () => {
      this.map.resize();
    });

    this.map.on('error', (event) => {
      console.error('Mapbox error:', event.error);
    });

    this.mapMarker = new mapboxgl.Marker({
      element: this.createMapMarker(),
      anchor: 'bottom',
    })
      .setLngLat(center)
      .addTo(this.map);
  }

  /** Takes the shared map back out of a card that is no longer settled. */
  unmountMap(panel) {
    const container = panel.el.querySelector('[data-experience-modal-map]');
    if (this.mapHost && container && this.mapHost.parentElement === container) {
      this.mapHost.remove();
    }
  }

  destroyMap() {
    this.mapMarker?.remove();
    this.mapMarker = null;
    this.map?.remove();
    this.map = null;
    this.mapHost?.remove();
    this.mapHost = null;
  }

  lockScroll() {
    this.pageScrollY = window.scrollY;
    // Hiding the page scrollbar widens the viewport and reflows the whole page
    // behind the backdrop; hold the layout still by padding the root with the
    // scrollbar's width (0 for overlay scrollbars) while the lock is on.
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty(
      '--experience-modal-scrollbar-pad',
      `${scrollbarWidth}px`,
    );
    document.documentElement.classList.add('is-experience-modal-open');
  }

  unlockScroll() {
    document.documentElement.classList.remove('is-experience-modal-open');
    document.documentElement.style.removeProperty(
      '--experience-modal-scrollbar-pad',
    );
    window.scrollTo({ top: this.pageScrollY, behavior: 'instant' });
  }

  /** Collapsed previews show this many lines, "See more" ending the last. */
  static DESCRIPTION_PREVIEW_LINES = 2;

  setDescription(root, value) {
    const wrap = root.querySelector('[data-experience-modal-description-wrap]');
    const span = root.querySelector('[data-experience-modal-description-text]');
    if (!wrap || !span) return;

    // Populate runs twice per card (summary, then detail), so every pass
    // starts back at the collapsed state.
    wrap._fullDescription = value || '';
    wrap._descriptionExpanded = false;
    wrap.classList.toggle('hidden', !value);
    this.renderDescription(wrap);

    // The card is in the document by now, but the dialog may still be
    // opening, so a one-shot measure can lie. The observer fires once real
    // layout exists, and again on resize, where the two-line cut lands
    // somewhere else.
    wrap._overflowObserver?.disconnect();
    wrap._overflowObserver = new ResizeObserver(() => {
      if (wrap.clientWidth === wrap._lastDescriptionWidth) return;
      this.renderDescription(wrap);
    });
    wrap._overflowObserver.observe(wrap);
  }

  /**
   * The design wants "See more" inline at the end of the second line, which
   * CSS line-clamp cannot do — it can only cut the text, not flow a control
   * after the cut. So the toggle lives inside the paragraph and the text is
   * trimmed in JS instead: a binary search for the longest prefix whose
   * "prefix… See more" still fits the preview height.
   */
  renderDescription(wrap) {
    const text = wrap.querySelector('[data-experience-modal-description]');
    const span = wrap.querySelector('[data-experience-modal-description-text]');
    const toggle = wrap.querySelector(
      '[data-experience-modal-description-toggle]',
    );
    const full = wrap._fullDescription || '';
    wrap._lastDescriptionWidth = wrap.clientWidth;

    span.textContent = full;
    if (!toggle) return;
    toggle.textContent = this.translate(
      wrap._descriptionExpanded ? 'see_less' : 'see_more',
    );
    toggle.setAttribute('aria-expanded', String(wrap._descriptionExpanded));

    if (wrap._descriptionExpanded) {
      toggle.classList.remove('hidden');
      return;
    }

    // Not laid out yet — the observer calls back when real widths exist.
    if (!wrap.clientWidth || !full) {
      toggle.classList.add('hidden');
      return;
    }

    const lineHeight = parseFloat(getComputedStyle(text).lineHeight);
    const budget =
      lineHeight * ExperienceModal.DESCRIPTION_PREVIEW_LINES + lineHeight / 2;
    const fits = () => text.getBoundingClientRect().height <= budget;

    if (fits()) {
      toggle.classList.add('hidden');
      return;
    }

    toggle.classList.remove('hidden');
    let low = 0;
    let high = full.length;
    while (low < high) {
      const mid = (low + high + 1) >> 1;
      span.textContent = `${full.slice(0, mid).trimEnd()}\u2026 `;
      if (fits()) low = mid;
      else high = mid - 1;
    }
    span.textContent = `${full.slice(0, low).trimEnd()}\u2026 `;
  }

  toggleDescription(button) {
    const wrap = button.closest('[data-experience-modal-description-wrap]');
    if (!wrap) return;

    wrap._descriptionExpanded = !wrap._descriptionExpanded;
    this.renderDescription(wrap);
  }

  setOptionalText(element, value, container = element) {
    const hasValue = Boolean(value);
    element.textContent = value || '';
    container.classList.toggle('hidden', !hasValue);
    container.classList.toggle('flex', hasValue && container !== element);
  }

  populateInclusions(root, experience) {
    const container = root.querySelector('[data-experience-modal-inclusions]');
    if (!container) return;

    container.innerHTML = '';

    const inclusions = experience.inclusions || [];
    const exclusions = experience.exclusions || [];

    if (inclusions.length) {
      const title = document.createElement('p');
      title.className = ' font-bold text-blue-4';
      title.textContent = this.translate('inclusions');
      container.appendChild(title);
      container.appendChild(this.buildBulletList(inclusions));
    }

    if (exclusions.length) {
      const title = document.createElement('p');
      title.className = ' font-bold pt-2 text-blue-4';
      title.textContent = this.translate('exclusions');
      container.appendChild(title);
      container.appendChild(this.buildBulletList(exclusions));
    }

    container.classList.toggle('hidden', container.innerHTML === '');
  }

  populateItinerary(root, experience) {
    const container = root.querySelector('[data-experience-modal-itinerary]');
    const wrapper = root.querySelector(
      '[data-experience-modal-itinerary-wrapper]',
    );
    if (!container || !wrapper) return;

    container.innerHTML = '';
    const itinerary = experience.itinerary;
    const items = itinerary?.itineraryItems || [];

    if (!itinerary || !items.length) {
      wrapper.classList.add('hidden');
      return;
    }

    wrapper.classList.remove('hidden');

    const metaList = document.createElement('ul');
    metaList.className = 'mb-4 list-disc pl-5';

    const duration = itinerary.duration?.fixedDurationInMinutes;
    if (duration) {
      metaList.appendChild(
        this.buildListItem(
          `${this.translate('duration')}: ${this.translate('minutes', {
            minutes: duration,
          })}`,
        ),
      );
    }

    metaList.appendChild(
      this.buildListItem(
        `${this.translate('skip_the_line')}: ${
          itinerary.skipTheLine ? this.translate('yes') : this.translate('no')
        }`,
      ),
    );
    metaList.appendChild(
      this.buildListItem(
        `${this.translate('private_tour')}: ${
          itinerary.privateTour ? this.translate('yes') : this.translate('no')
        }`,
      ),
    );

    if (itinerary.maxTravelersInSharedTour) {
      metaList.appendChild(
        this.buildListItem(
          `${this.translate('max_travelers')}: ${
            itinerary.maxTravelersInSharedTour
          }`,
        ),
      );
    }

    container.appendChild(metaList);

    const itemsList = document.createElement('ol');
    itemsList.className = 'list-decimal pl-5';

    items.forEach((item) => {
      const li = document.createElement('li');
      li.className = 'mb-3';

      const label = item.passByWithoutStopping
        ? this.translate('pass_by')
        : this.translate('stop_at');
      const admission =
        item.admissionIncluded === 'YES'
          ? ` (${this.translate('admission_included')})`
          : '';

      const title = document.createElement('strong');
      title.className = 'pt-2';
      title.textContent = `${label}${admission}`;
      li.appendChild(title);

      if (item.description) {
        const description = document.createElement('p');
        description.className = 'pt-2';
        description.textContent = item.description.replace(/\s+/g, ' ').trim();
        li.appendChild(description);
      }

      if (item.duration?.fixedDurationInMinutes) {
        const durationText = document.createElement('p');
        durationText.className = 'text-sm text-neutral-5';
        durationText.textContent = `${this.translate(
          'duration',
        )}: ${this.translate('minutes', {
          minutes: item.duration.fixedDurationInMinutes,
        })}`;
        li.appendChild(durationText);
      }

      itemsList.appendChild(li);
    });

    container.appendChild(itemsList);
  }

  populateLanguages(root, experience) {
    const container = root.querySelector('[data-experience-modal-languages]');
    const wrapper = root.querySelector(
      '[data-experience-modal-language-wrapper]',
    );
    if (!container || !wrapper) return;

    container.innerHTML = '';
    const guides = experience.languageGuides || [];

    if (!guides.length) {
      wrapper.classList.add('hidden');
      return;
    }

    wrapper.classList.remove('hidden');

    const list = document.createElement('ul');
    list.className = 'list-disc pl-5';

    guides.forEach((guide) => {
      const name = this.languageName(guide.language);
      const type = guide.type ? ` (${guide.type})` : '';
      list.appendChild(
        this.buildListItem(`${this.translate('guided_in')} ${name}${type}`),
      );
    });

    container.appendChild(list);
  }

  buildBulletList(items) {
    const list = document.createElement('ul');
    list.className = 'list-disc pl-5';
    items.forEach((item) => list.appendChild(this.buildListItem(item)));
    return list;
  }

  buildListItem(text) {
    const li = document.createElement('li');
    li.textContent = text;
    return li;
  }

  languageName(code) {
    const names = {
      en: 'English',
      es: 'Spanish',
      fr: 'French',
      de: 'German',
      it: 'Italian',
      pt: 'Portuguese',
      nl: 'Dutch',
      zh: 'Chinese',
      ja: 'Japanese',
      ko: 'Korean',
      ar: 'Arabic',
      ru: 'Russian',
      hi: 'Hindi',
    };
    return (
      names[code] || code?.toUpperCase() || this.translate('not_available')
    );
  }

  /**
   * Star levels the histogram draws, top down.
   *
   * All five, always, even the ones with nothing in them. The median reviewed
   * product has 7 reviews, so four empty tracks is a real readability problem —
   * but both Figma frames show five rows unconditionally and so does the
   * Viator-certified `RatingDistribution.tsx`, and a certified implementation
   * is not something to diverge from on one surface only. If it is worth
   * changing it is worth changing in both codebases.
   */
  static REVIEW_STAR_LEVELS = [5, 4, 3, 2, 1];

  /** Provider codes with a localised name and a wordmark in `assets/`. */
  static REVIEW_PROVIDERS = ['VIATOR', 'TRIPADVISOR'];

  /**
   * Distinct traveller photos drawn per review, after `dedupePhotos`.
   *
   * The frames show three and the strip scrolls, so this is a memory cap in the
   * spirit of `GALLERY_LIMIT`, not a design one — this modal has already been
   * killed once by iOS Safari for decoding too many bitmaps.
   */
  static REVIEW_PHOTO_LIMIT = 10;

  /**
   * The rendition width the photo strip actually wants.
   *
   * The squares are 136px on mobile and 160px on desktop, so ~320px covers a
   * 2x screen. Picking the largest rendition instead — which is what the
   * portal's gallery does, because it opens them full-bleed — hands a
   * 1024x1365 JPEG to a 136px box and decodes ~5.6MB of bitmap per photo.
   */
  static REVIEW_PHOTO_TARGET_WIDTH = 320;

  /** Built on first use — see `formatReviewDate`. */
  static dateFormat = null;

  /**
   * Paints one five-star row: `filled` stars in Blue/4, the rest outlined in
   * Blue/3.
   *
   * Not the same technique as the header rating, which clips a Yellow/4 overlay
   * to a fraction of its width. These are whole stars, because that is what the
   * histogram rows and the per-review cards are — a level, not a score.
   *
   * Every class it touches is written literally into
   * `snippets/experience-review-stars.liquid`: Tailwind compiles utilities
   * ahead of time and scans only `.liquid`, so a class that exists solely as a
   * string in this file is never built and the star silently renders wrong.
   */
  static renderStars(row, filled) {
    if (!row) return;
    const whole = Math.max(0, Math.min(5, Math.round(Number(filled) || 0)));
    row
      .querySelectorAll('[data-experience-review-star]')
      .forEach((star, index) => {
        const on = index < whole;
        star.classList.toggle('fill-current', on);
        star.classList.toggle('text-blue-4', on);
        star.classList.toggle('fill-none', !on);
        star.classList.toggle('text-blue-3', !on);
      });
  }

  /**
   * Rating buckets indexed by rounded star level.
   *
   * **Never render these in wire order.** The back-end DTOs document them as
   * arriving highest-first (5 → 1) and state that clients "may render the
   * histogram directly and do NOT need to sort". Live data says the opposite:
   * 20,000 of 20,000 sampled products arrive ascending, 1 → 5, so a client that
   * trusts the contract draws the histogram upside down. The back-end tests
   * pass because both fixtures hand-seed descending order — they pin the
   * fixture, not Viator. Indexing by level is correct whichever way that defect
   * is eventually resolved, and it also absorbs the duplicate buckets Viator
   * can send.
   */
  static bucketsByLevel(buckets) {
    const byLevel = new Map();
    (Array.isArray(buckets) ? buckets : []).forEach((bucket) => {
      if (bucket?.rating == null) return;
      const level = Math.round(Number(bucket.rating));
      if (!(level >= 1 && level <= 5)) return;
      byLevel.set(
        level,
        (byLevel.get(level) || 0) + (Number(bucket.count) || 0),
      );
    });
    return byLevel;
  }

  /**
   * One entry per real photo, at a sensible size.
   *
   * `photos[]` on a review is NOT a list of photos — it is a flat list of CDN
   * **renditions**, roughly nine per image, ordered smallest first and repeated
   * for every photo the reviewer uploaded. One Tripadvisor review on the Mokapu
   * Luau sends 70 entries that are 7 pictures. Rendering the array as-is drew
   * the same balloon seven times in a row, the first two visibly blurry because
   * the run starts at a 50x50 thumbnail and a `?w=100` dynamic crop.
   *
   * Also note `thumbnail` is always null here, unlike `images[]` on the detail
   * response — there is no pre-picked small rendition to fall back on, which is
   * why the size has to be chosen rather than read.
   *
   * Photos are grouped by their path with the rendition marker (`photo-t`,
   * `photo-w`, `photo-m/1280`) removed, so every variant of one image collapses
   * to a single key. Ordering is deliberately not used as the signal: the runs
   * happen to ascend by size today, but nothing on the wire promises that.
   *
   * The certified portal's `dedupeTravelerPhotos` takes exactly ONE photo per
   * review (the largest variant) because its band shows one tile per reviewer.
   * This strip shows a reviewer's whole set, so it groups instead.
   */
  static dedupePhotos(photos) {
    const byImage = new Map();

    (Array.isArray(photos) ? photos : []).forEach((photo) => {
      const url = photo?.url;
      if (!url) return;

      let key = url;
      try {
        key = new URL(url, window.location.origin).pathname
          .split('/')
          .filter(
            (segment) =>
              segment &&
              !/^photo-[a-z0-9]+$/i.test(segment) &&
              !/^\d+$/.test(segment),
          )
          .join('/');
      } catch {
        // A relative or malformed URL is its own group rather than a thrown error.
      }

      if (!byImage.has(key)) byImage.set(key, []);
      byImage.get(key).push(photo);
    });

    return [...byImage.values()].map((variants) => {
      // The smallest rendition that still covers the box on a 2x screen, and
      // only the biggest available when nothing reaches it.
      const wide = variants.filter(
        (variant) =>
          (Number(variant.width) || 0) >=
          ExperienceModal.REVIEW_PHOTO_TARGET_WIDTH,
      );
      if (wide.length) {
        return wide.reduce((a, b) => (b.width < a.width ? b : a));
      }
      return variants.reduce((a, b) =>
        (Number(b.width) || 0) * (Number(b.height) || 0) >
        (Number(a.width) || 0) * (Number(a.height) || 0)
          ? b
          : a,
      );
    });
  }

  /** "Viator and Tripadvisor" in the page's own language. */
  static formatList(items) {
    if (items.length < 2) return items.join('');
    try {
      return new Intl.ListFormat(document.documentElement.lang || undefined, {
        style: 'long',
        type: 'conjunction',
      }).format(items);
    } catch {
      return items.join(', ');
    }
  }

  /**
   * A review's date.
   *
   * The frame draws `03/12/2026`, which is ambiguous between DD/MM and MM/DD.
   * The numerals are the design's and the *order* is the locale's: the API
   * sends ISO-8601, and `Intl` resolves the rest from the page language rather
   * than the theme picking a side.
   */
  static formatReviewDate(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    if (!ExperienceModal.dateFormat) {
      const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
      try {
        ExperienceModal.dateFormat = new Intl.DateTimeFormat(
          document.documentElement.lang || undefined,
          options,
        );
      } catch {
        // A malformed `<html lang>` is a RangeError, not a reason to lose the date.
        ExperienceModal.dateFormat = new Intl.DateTimeFormat(
          undefined,
          options,
        );
      }
    }
    return ExperienceModal.dateFormat.format(date);
  }

  /**
   * A provider's display name.
   *
   * `raw` decides what an unmapped code does, and the two callers want opposite
   * things. The aggregate legal line drops it, so that sentence never renders a
   * raw, un-localised wire value; the per-source rows and the per-review byline
   * fall back to it, so attribution is never simply missing. The certified
   * build splits them exactly this way — deliberately. Do not unify them.
   */
  providerLabel(provider, { raw = false } = {}) {
    const code = String(provider || '')
      .trim()
      .toUpperCase();
    if (!code) return '';
    if (ExperienceModal.REVIEW_PROVIDERS.includes(code)) {
      return this.translate(`reviews_provider_${code}`);
    }
    return raw ? String(provider).trim() : '';
  }

  /**
   * The §17 attribution line under the header stars, built from the providers
   * that actually contributed.
   *
   * It used to name Viator and Tripadvisor on every experience, while only 52%
   * of reviewed products carry both — so on the rest we credited a provider
   * that contributed nothing.
   */
  populateRatingSource(root, experience) {
    const wrap = root.querySelector('[data-experience-modal-rating-source]');
    if (!wrap) return;

    const sources = Array.isArray(experience.reviewSources)
      ? experience.reviewSources
      : [];
    const present = new Set(
      sources
        .map((source) =>
          String(source?.provider || '')
            .trim()
            .toUpperCase(),
        )
        .filter(Boolean),
    );
    const named = [...present]
      .map((code) => this.providerLabel(code))
      .filter(Boolean);

    // Nothing mapped — either the detail has not landed yet, or every code on
    // the wire is new to us — so the approved generic sentence stands in.
    // Attribution is never dropped here, only made less specific.
    wrap.querySelector(
      '[data-experience-modal-rating-source-text]',
    ).textContent = named.length
      ? this.translate('rating_source_providers', {
          providers: ExperienceModal.formatList(named),
        })
      : this.translate('rating_source');

    // Both marks stay up while the sentence is generic, because that sentence
    // names both. Once a provider list exists, only its own logos survive.
    wrap
      .querySelectorAll('[data-experience-modal-source-logo]')
      .forEach((logo) => {
        const known = present.has(logo.dataset.experienceModalSourceLogo);
        logo.classList.toggle('hidden', named.length > 0 && !known);
      });
  }

  /**
   * The reviews band: heading, aggregate, histogram, per-source attribution,
   * and the lazy testimonial list.
   *
   * Runs on both `populate` passes, so everything here has to be safe against
   * the summary shape the openers carry — which has a rating and a count but
   * neither review array.
   */
  populateReviews(panel, experience) {
    const section = panel.el.querySelector('[data-experience-modal-reviews]');
    if (!section) return;

    const total = Number(experience.reviews_amount) || 0;
    // Rule 1: no reviews, no section — never an empty state. 41% of the live
    // catalogue has zero reviews, and "no reviews yet" would print a negative
    // signal on all of them for being new to the feed rather than unloved.
    // `hidden` and `flex` are toggled as a pair so only one display utility is
    // ever on the element and their order in the stylesheet cannot matter.
    section.classList.toggle('hidden', total <= 0);
    section.classList.toggle('flex', total > 0);
    if (total <= 0) return;

    const rating = parseFloat(experience.rating) || 0;
    // The average always prints. No suppression at low counts — a 5.0 off a
    // single review renders as 5.0; hiding it below five reviews was considered
    // and declined.
    // Always one decimal, unlike the header numeral, which drops `.0` on a whole
    // number. A bare `5` beside a source row reading `5.0 via Viator` looks like
    // two different figures; the certified component pins a minimum of one
    // fraction digit for the same reason, and the frame draws `4.9`.
    section.querySelector(
      '[data-experience-modal-reviews-average]',
    ).textContent = rating.toFixed(1);
    ExperienceModal.renderStars(
      section.querySelector('[data-experience-review-stars="average"]'),
      rating,
    );
    section.querySelector('[data-experience-modal-reviews-count]').textContent =
      this.translate(
        total === 1 ? 'reviews_based_on_one' : 'reviews_based_on_other',
        { count: formatNumber(total) },
      );

    this.populateHistogram(section, experience);
    this.populateReviewSources(section, experience);
    this.watchReviews(panel, experience);
  }

  populateHistogram(section, experience) {
    const list = section.querySelector(
      '[data-experience-modal-reviews-histogram]',
    );
    const template = document.querySelector(
      '[data-experience-review-bar-template]',
    );
    if (!list || !template) return;

    const counts = ExperienceModal.bucketsByLevel(
      experience.ratingDistribution,
    );
    // The denominator is the buckets' own sum, NOT `reviews_amount`. The two
    // differ — the product-wide count against a filtered subset — and using the
    // count makes every bar a sliver. The certified component sums the buckets,
    // and the frame's own 5-star bar is 438.62/482 ≈ 91%, which is 1672 ÷ ~1833
    // (the buckets) and not 1672 ÷ 12,847 (the count).
    const total = ExperienceModal.REVIEW_STAR_LEVELS.reduce(
      (sum, level) => sum + (counts.get(level) || 0),
      0,
    );

    list.replaceChildren();
    ExperienceModal.REVIEW_STAR_LEVELS.forEach((level) => {
      const count = counts.get(level) || 0;
      const row = template.content.cloneNode(true).firstElementChild;

      ExperienceModal.renderStars(
        row.querySelector('[data-experience-review-stars="bar"]'),
        level,
      );
      // A near-zero bucket needs no special case: a sub-pixel width inside a
      // rounded, clipped track simply does not paint, which is what the frame
      // shows for its 2-star row.
      row.querySelector('[data-experience-review-bar-fill]').style.width = `${
        total > 0 ? (count / total) * 100 : 0
      }%`;
      row.querySelector('[data-experience-review-bar-count]').textContent =
        this.translate('reviews_bar_count', { count: formatNumber(count) });
      row.setAttribute(
        'aria-label',
        this.translate('reviews_bar_label', {
          rating: level,
          count: formatNumber(count),
        }),
      );
      list.append(row);
    });
  }

  /**
   * One row per provider, each with its own score and volume.
   *
   * They are not decoration for the blended average: the two providers
   * routinely disagree — 4.4 across 678 against 4.8 across 2,332 on the same
   * tour, blending to 4.7 — which is exactly why §17 wants them named
   * separately rather than under one number.
   */
  populateReviewSources(section, experience) {
    const list = section.querySelector(
      '[data-experience-modal-reviews-sources]',
    );
    const template = document.querySelector(
      '[data-experience-review-source-template]',
    );
    if (!list || !template) return;

    list.replaceChildren();
    const sources = Array.isArray(experience.reviewSources)
      ? experience.reviewSources
      : [];

    sources.forEach((source) => {
      // §17 needs a label, so a source with no provider has nothing to
      // attribute and is dropped. An unknown code keeps its raw wire value.
      const label = this.providerLabel(source?.provider, { raw: true });
      if (!label) return;

      const row = template.content.cloneNode(true).firstElementChild;
      const average = Number(source?.averageRating);
      const stars = row.querySelector(
        '[data-experience-review-stars="source"]',
      );
      const averageEl = row.querySelector(
        '[data-experience-review-source-average]',
      );

      if (Number.isFinite(average)) {
        ExperienceModal.renderStars(stars, average);
        averageEl.textContent = average.toFixed(1);
      } else {
        stars.remove();
        averageEl.remove();
      }

      const count = Number(source?.totalCount) || 0;
      row.querySelector('[data-experience-review-source-label]').textContent =
        this.translate('reviews_source_attribution', {
          provider: label,
        });
      row.querySelector('[data-experience-review-source-count]').textContent =
        this.translate(
          count === 1
            ? 'reviews_source_count_one'
            : 'reviews_source_count_other',
          { count: formatNumber(count) },
        );
      list.append(row);
    });
  }

  /**
   * Arms the lazy fetch of the testimonial list.
   *
   * Deliberately not fired on open. The list is a separate endpoint precisely
   * because it hits a rate-limited Viator API — 30 requests a minute, shared
   * with the redemption portal — and this band is the last thing in a long
   * modal. Fetching for every card that gets opened would spend that budget on
   * reviews nobody scrolled to, and would couple the modal's open latency to
   * Viator's.
   */
  watchReviews(panel, experience) {
    // `populate` runs twice per card, once on the summary and once on the
    // detail. The second pass must not re-arm the observer or wipe a list that
    // has already loaded.
    if (panel.reviewsFor === experience.id) return;
    panel.reviewsFor = experience.id;
    panel.reviewsPage = 0;
    panel.reviewsBusy = false;

    const section = panel.el.querySelector('[data-experience-modal-reviews]');
    const list = section?.querySelector('[data-experience-modal-reviews-list]');
    const more = section?.querySelector('[data-experience-modal-reviews-more]');
    if (!section || !list || !more) return;

    list.replaceChildren();
    more.hidden = true;
    more.onclick = () => this.loadReviews(panel, experience);

    panel.reviewsObserver?.disconnect();
    panel.reviewsObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        panel.reviewsObserver?.disconnect();
        panel.reviewsObserver = null;
        this.loadReviews(panel, experience);
      },
      // The card scrolls inside its own box, so the dialog's scroller is the
      // root — the viewport would never intersect it.
      { root: panel.scroll, rootMargin: '300px 0px' },
    );
    panel.reviewsObserver.observe(section);
  }

  async loadReviews(panel, experience) {
    if (panel.reviewsBusy) return;

    const section = panel.el.querySelector('[data-experience-modal-reviews]');
    const list = section?.querySelector('[data-experience-modal-reviews-list]');
    const more = section?.querySelector('[data-experience-modal-reviews-more]');
    const template = document.querySelector(
      '[data-experience-review-card-template]',
    );
    const url = this.reviewsUrl(experience.id, panel.reviewsPage);
    if (!list || !more || !template || !url) return;

    panel.reviewsBusy = true;
    more.disabled = true;
    try {
      const data = await fetchJson(url);
      // Closed, or swapped onto another card, while the page was in flight.
      if (this.panel !== panel) return;

      const reviews = Array.isArray(data?.content) ? data.content : [];
      reviews.forEach((review) =>
        list.append(this.buildReviewCard(template, review)),
      );

      // `totalPages` arrives already capped to the reachable count (6 pages of
      // 5), so — unlike the portal, which has to clamp it client-side — it can
      // be rendered verbatim: every page it advertises is fetchable.
      panel.reviewsPage += 1;
      more.hidden = panel.reviewsPage >= (Number(data?.totalPages) || 0);
    } catch (error) {
      console.error('Failed to load experience reviews:', error);
      more.hidden = true;
    } finally {
      panel.reviewsBusy = false;
      more.disabled = false;
    }
    // Nothing above caches an empty answer, and that is the point. The endpoint
    // never 503s: a Viator outage or an exhausted rate limit comes back 200
    // with empty `content` and `Cache-Control: no-store`. "No reviews came
    // back" therefore does not mean "this experience has no reviews" —
    // `reviews_amount` off the detail response is the truth — so the band keeps
    // its aggregate and simply asks again the next time it is opened.
  }

  /**
   * The reviews endpoint for one experience.
   *
   * Read off the section element at call time rather than captured once: the
   * modal is built lazily and outlives variant changes, which rewrite
   * `data-product-sku` in place.
   */
  reviewsUrl(experienceCode, page) {
    // A collection page has no explore section round its modal: it wraps the dialog in an element
    // carrying the same two values, and sets the SKU to the open tab's gift (assets/ew-collection.js).
    const config = (
      this.modal
        ?.closest('.section-explore-experiences')
        ?.querySelector('.explore_experiences') ||
      this.modal?.closest('[data-experience-modal-config]')
    )?.dataset;
    const baseUrl = config?.apiBaseUrl;
    const sku = config?.productSku;
    if (!baseUrl || !sku || !experienceCode) return null;

    // `page` is the only parameter this endpoint takes. There is no `size`: the
    // server fixes it at 5 so the theme and the redemption portal share cache
    // entries and warm each other, and `ratings` is not exposed either.
    return `${baseUrl}/products/${encodeURIComponent(
      sku,
    )}/experience/${encodeURIComponent(experienceCode)}/reviews?page=${page}`;
  }

  buildReviewCard(template, review) {
    const card = template.content.cloneNode(true).firstElementChild;
    const rating = Number(review?.rating) || 0;

    const stars = card.querySelector('[data-experience-review-stars="card"]');
    ExperienceModal.renderStars(stars, rating);
    stars.setAttribute('role', 'img');
    stars.setAttribute(
      'aria-label',
      this.translate('reviews_rating_aria', { rating }),
    );

    card.querySelector('[data-experience-review-title]').textContent =
      review?.title || '';
    card.querySelector('[data-experience-review-name]').textContent =
      review?.userName || '';
    card.querySelector('[data-experience-review-text]').textContent =
      review?.text || '';

    const avatar = card.querySelector('[data-experience-review-avatar]');
    if (review?.avatarUrl) {
      avatar.src = review.avatarUrl;
      avatar.classList.remove('hidden');
    } else {
      avatar.remove();
    }

    // Provider on the byline: §17 attribution again, at the row level. An
    // unknown code shows raw here rather than vanishing.
    const provider = this.providerLabel(review?.provider, { raw: true });
    const attribution = provider
      ? this.translate('reviews_source_attribution', { provider })
      : '';
    const date = ExperienceModal.formatReviewDate(review?.publishedDate);
    card.querySelector('[data-experience-review-byline]').textContent =
      date && attribution
        ? this.translate('reviews_byline', { date, provider })
        : date || attribution;

    // A translated review presented as the reviewer's own words is exactly what
    // certification cares about, so the notice is mirrored from `ReviewCard`.
    // The supplier's `ownerResponse` is NOT rendered: the certified build
    // carries the strings for one and deliberately draws nothing, by a product
    // decision to show original review copy only.
    card
      .querySelector('[data-experience-review-translated]')
      .classList.toggle('hidden', review?.machineTranslated !== true);

    // See `dedupePhotos` — the wire sends renditions, not photos.
    const photosList = card.querySelector('[data-experience-review-photos]');
    const photoTemplate = document.querySelector(
      '[data-experience-review-photo-template]',
    );
    if (photoTemplate) {
      ExperienceModal.dedupePhotos(review?.photos)
        .slice(0, ExperienceModal.REVIEW_PHOTO_LIMIT)
        .forEach((photo) => {
          const item = photoTemplate.content.cloneNode(true).firstElementChild;
          item.querySelector('[data-experience-review-photo]').src = photo.url;
          photosList.append(item);
        });
    }

    return card;
  }

  translate(key, variables = {}) {
    const strings = window.ExperienceModalStrings || ExperienceModal.STRINGS;
    let text = strings[key] || key;
    Object.entries(variables).forEach(([name, value]) => {
      text = text.replace(
        new RegExp(`{{\\s*${name}\\s*}}`, 'g'),
        String(value),
      );
    });
    return text;
  }

  static STRINGS = {
    description: 'Description',
    overview: 'Overview',
    itinerary: 'Itinerary',
    language_details: 'Language',
    inclusions: 'Inclusions',
    exclusions: 'Exclusions',
    duration: 'Duration',
    skip_the_line: 'Skip the line',
    private_tour: 'Private tour',
    max_travelers: 'Max travelers',
    guided_in: 'Guided in',
    yes: 'Yes',
    no: 'No',
    admission_included: 'Admission included',
    pass_by: 'Pass by',
    stop_at: 'Stop at',
    minutes: '{{ minutes }} minutes',
    not_available: 'Not available',
    see_more: 'See more',
    see_less: 'See less',
  };
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () =>
    SectionProductExploreExperiences.onLoad(),
  );
} else {
  SectionProductExploreExperiences.onLoad();
}

document.addEventListener('everwow:variantchanged', (event) => {
  const { sku } = event.detail || {};
  if (!sku) return;

  document.querySelectorAll('.explore_experiences').forEach((section) => {
    section.dataset.productSku = sku;
  });

  SectionProductExploreExperiences.reloadForVariant(sku);
  SectionProductExploreExperiences.updateExperiencesPrices();
});
